// ─────────────────────────────────────────────────────────────────────────────
// Practice-area hubs — the 13 entry points into India-specific legal work.
//
// LAST REVIEWED
//   2026-08-19.
//
// SOURCING POSTURE
//   These pages are assembled ENTIRELY from assets that already exist in this
//   repo and in the LexAI backend. Nothing here is a forward-looking claim.
//
//   Every `StatuteRef` carries a `source` naming where the citation was taken
//   from, so any row can be audited without leaving the codebase:
//
//     "statute-library"      backend/app/seeds/statutes.py — 54 sections across
//                            10 Acts, seeded into LexVio's statute library.
//                            `plain` condenses that seed's `explanation_plain`.
//     "compliance-calendar"  src/app/calendar/_data/deadlines.ts — the
//                            `statutoryRef` of a live recurring obligation.
//     "limitation-act"       src/app/tools/limitation-period-finder/_data/
//                            entries.ts — quoted from the India Code bare Act.
//     "dpdp-dataset"         src/app/tools/dpdp-readiness-quiz/_data/
//                            questions.ts — cited to the DPDP Act 2023 by
//                            section and the DPDP Rules 2025 by rule.
//
//   Every slug/href in a `related*` array was checked against the file it came
//   from on the review date. The renderers resolve them through the source
//   datasets (FEATURES, ENTRIES, fallbackSummary, TOOL_INDEX, SOLUTION_INDEX,
//   PRODUCT_INDEX) and DROP anything that no longer resolves — a stale slug
//   degrades to an absent card, never to a 404.
//
//   A sparse area is left sparse on purpose. Intellectual Property carries
//   three statute rows because the seeded statute library contains exactly one
//   IP Act (Trade Marks, 1999); padding it with the Patents Act or the
//   Copyright Act would be a claim this codebase cannot support.
//
//   None of this is legal advice. See DISCLAIMER, which every hub renders.
// ─────────────────────────────────────────────────────────────────────────────

import type { Regulator } from "@/app/calendar/_data/deadlines";

export const REVIEWED_ON = "2026-08-19";
export const REVIEWED_ON_LONG = "19 August 2026";

export const DISCLAIMER =
  "These hubs point at statutory text, free calculators and product capabilities. They are not legal advice, they do not create an advocate-client relationship, and they are no substitute for reading the bare Act as currently amended. Indian law is fact- and state-specific — stamp duty, registration and several employment obligations vary by state, and limitation turns on facts a web page cannot know. Take advice on your own facts before acting.";

/** Where a statutory citation on these pages was taken from. */
export type StatuteSource =
  | "statute-library"
  | "compliance-calendar"
  | "limitation-act"
  | "dpdp-dataset";

export const SOURCE_LABELS: Record<StatuteSource, string> = {
  "statute-library": "Statute library",
  "compliance-calendar": "Compliance calendar",
  "limitation-act": "Limitation Act dataset",
  "dpdp-dataset": "DPDP dataset",
};

export type StatuteRef = {
  /** Full Act name, as the source names it. */
  act: string;
  /** Section / article / regulation pointer, as cited. */
  section: string;
  /** The provision's own heading — not our gloss. */
  heading: string;
  /** One plain sentence, condensed from the source's own explanation. */
  plain: string;
  source: StatuteSource;
};

export type Faq = { q: string; a: string };

export type PracticeArea = {
  /** URL key — /practice-areas/<slug>. */
  slug: string;
  /** H1 and card title. */
  title: string;
  /** Compact label for nav, breadcrumbs and prev/next. */
  short: string;
  /** 2-3 sentences. India-specific and concrete. Card + meta description. */
  blurb: string;
  /** Answer-first: what this area actually covers under Indian law. */
  covers: ReadonlyArray<string>;
  statutes: ReadonlyArray<StatuteRef>;
  /** Slugs in src/app/features/_data/features.ts → /features/<slug>. */
  relatedFeatureSlugs: ReadonlyArray<string>;
  /** Keys of TOOL_INDEX — all live routes. */
  relatedToolHrefs: ReadonlyArray<string>;
  /** Slugs in the limitation dataset → /tools/limitation-period-finder/<slug>. */
  relatedLimitationSlugs: ReadonlyArray<string>;
  /** Slugs in src/app/library/_data/source.ts → /library/<slug>. */
  relatedTemplateSlugs: ReadonlyArray<string>;
  /** Keys of SOLUTION_INDEX → /solutions/<slug>. */
  relatedSolutionHrefs: ReadonlyArray<string>;
  /** Keys of PRODUCT_INDEX → /product/<slug>. */
  relatedProductHrefs: ReadonlyArray<string>;
  /** Regulators from the compliance calendar whose deadlines bite here. */
  relatedCalendarRegulators: ReadonlyArray<Regulator>;
  faqs: ReadonlyArray<Faq>;
  /** Rendered verbatim where the assembled material is genuinely thin. */
  sparseNote?: string;
};

// ── Route indexes ───────────────────────────────────────────────────────────
// Labels for routes that do not live in a typed dataset of their own. Each key
// is a real route in this app, verified against the route tree on the review
// date. Renderers iterate these maps, so an unknown key renders nothing.

export const TOOL_INDEX: Record<string, { label: string; blurb: string }> = {
  "/tools/gst-calculator": {
    label: "GST Calculator",
    blurb:
      "Forward and reverse GST, with the CGST/SGST split for intra-state supply and IGST for inter-state.",
  },
  "/tools/tds-rate-finder": {
    label: "TDS Rate Finder",
    blurb:
      "Section-wise TDS rates for FY 2025-26 — salary, rent, professional fees, contractors and 20+ more.",
  },
  "/tools/nda-generator": {
    label: "NDA Generator",
    blurb:
      "Indian-law NDA in five templates — mutual, one-way, employment, vendor and IP-heavy.",
  },
  "/tools/stamp-duty-calculator": {
    label: "Stamp Duty Calculator",
    blurb:
      "State-by-state duty for sale deeds, gift deeds, leases and loan agreements. 22 states covered.",
  },
  "/tools/hsn-sac-finder": {
    label: "HSN / SAC Finder",
    blurb: "Find the GST code and rate for any product or service.",
  },
  "/tools/gstin-validator": {
    label: "GSTIN Validator",
    blurb:
      "Validate a GSTIN's checksum and decode state code, PAN and entity type — runs in your browser.",
  },
  "/tools/income-tax-section-mapper": {
    label: "Income Tax Act Section Mapper",
    blurb:
      "Where each Income-tax Act, 1961 section lands in the Income-tax Act, 2025 from 1 April 2026.",
  },
  "/tools/dpdp-readiness-quiz": {
    label: "DPDP Readiness Quiz",
    blurb:
      "15-question weighted self-assessment against the DPDP Act 2023 and the DPDP Rules 2025.",
  },
  "/tools/limitation-period-finder": {
    label: "Limitation Period Finder",
    blurb:
      "Periods quoted from the bare Limitation Act, 1963, plus the arbitration, cheque-bounce, IBC and consumer deadlines.",
  },
  "/tools/income-tax-slabs": {
    label: "Income Tax Slab Rates",
    blurb:
      "New and old regime side by side for FY 2024-25 to FY 2026-27, with rebate, surcharge and cess.",
  },
  "/tools/cost-inflation-index": {
    label: "Cost Inflation Index",
    blurb:
      "Every notified CII with an indexed-cost calculator — and the 23 July 2024 withdrawal caveat.",
  },
  "/tools/interest-calculator": {
    label: "Interest & Late Fee Calculator",
    blurb:
      "Interest under 234A/234B/234C, CGST s.50, TDS 201(1A) and the 234E fee, each with its citation.",
  },
  "/tools/roc-forms": {
    label: "ROC Form Finder",
    blurb:
      "The nine MCA/ROC filings a company or LLP must diarise, with the due rule and late-fee ladder.",
  },
  "/calendar": {
    label: "Compliance Deadline Calendar",
    blurb:
      "Every recurring statutory due date, month by month, each carrying its own citation.",
  },
  "/library": {
    label: "Free Contract Templates",
    blurb:
      "India-first templates, free to copy, modify and use commercially, without attribution.",
  },
};

export const SOLUTION_INDEX: Record<string, { label: string; blurb: string }> = {
  "/solutions/founders": {
    label: "Founders",
    blurb: "Stop signing investor docs you don't fully understand.",
  },
  "/solutions/freelancers": {
    label: "Freelancers",
    blurb: "Get paid. Don't get sued.",
  },
  "/solutions/ca-cs": {
    label: "CA / CS",
    blurb: "AI for the contracts your clients keep sending you.",
  },
  "/solutions/advocates": {
    label: "Advocates",
    blurb: "Court research in seconds. Drafts in minutes.",
  },
  "/solutions/agencies": {
    label: "Agencies",
    blurb: "MSAs, SOWs and retainers — reviewed in seconds.",
  },
  "/solutions/students": {
    label: "Students",
    blurb: "Learn law with AI — built for students.",
  },
  "/solutions/smb": {
    label: "Small Businesses",
    blurb: "Legal protection without the legal fees.",
  },
  "/solutions/law-firms": {
    label: "Law Firms",
    blurb: "Deliver more client work with white-label AI.",
  },
  "/solutions/financial-institutions": {
    label: "Financial Institutions",
    blurb: "AI-native SEBI, RBI and AMFI compliance.",
  },
  "/solutions/enterprise": {
    label: "Enterprise / MNC",
    blurb: "Custom AI. Unlimited seats. Your data, your cloud.",
  },
};

export const PRODUCT_INDEX: Record<string, { label: string; blurb: string }> = {
  "/product/legal-ai": {
    label: "Legal AI",
    blurb:
      "Contract review, AI redlining, court research, fix suggestions and the Legal Health Score.",
  },
  "/product/compliance-ai": {
    label: "Compliance AI",
    blurb:
      "SEBI, RBI, MCA/ROC and GST monitoring with alerts ahead of every deadline.",
  },
  "/product/tax-ai": {
    label: "Tax AI",
    blurb:
      "Income tax, TDS, GST, IFRS and IndAS read in the context of your own contracts and filings.",
  },
  "/product/workflow-agents": {
    label: "Workflow Agents",
    blurb:
      "No-code agents that automate multi-step legal, compliance and tax workflows.",
  },
  "/product/global": {
    label: "Global Layer",
    blurb:
      "Cross-border research, foreign filings and multi-jurisdiction analysis.",
  },
};

// ── The 13 areas ────────────────────────────────────────────────────────────

export const AREAS: ReadonlyArray<PracticeArea> = [
  // ── 1 ─────────────────────────────────────────────────────────────────────
  {
    slug: "litigation",
    title: "Litigation",
    short: "Litigation",
    blurb:
      "Indian civil litigation runs on a clock you cannot negotiate with: s.3 of the Limitation Act, 1963 requires a court to dismiss a time-barred suit even where limitation was never pleaded as a defence, and s.5 condonation reaches appeals and applications but never suits. Most commercial litigation is contract litigation — damages under s.73 of the Indian Contract Act, 1872, the s.74 ceiling on stipulated penalties, and specific performance under the Specific Relief Act, 1963. This hub gathers the periods, the provisions and the LexVio capabilities that read a judgment or a contract before the clock runs out.",
    covers: [
      "Litigation as an Indian practice area is really three clocks running at once. The first is the Schedule to the Limitation Act, 1963: Article 113 gives three years for any suit with no article of its own, Article 116 gives ninety days for a first appeal to a High Court and thirty days to any other court, Article 123 gives thirty days to set aside an ex parte decree, and Article 136 gives twelve years to execute a decree. The second is the substantive statute the claim arises under. The third is the procedural clock inside the court — restoration, review, revision and substitution each carry their own article.",
      "What is actually fought in Indian commercial courts is largely contract. Section 73 of the Indian Contract Act, 1872 gives compensation for loss that naturally arose from the breach; s.74 converts a stipulated penalty into reasonable compensation not exceeding the amount named, which is why liquidated-damages drafting matters long before a plaint is filed. Specific performance is governed by ss.10 and 14 of the Specific Relief Act, 1963, and s.20A bars injunctions that would impede an infrastructure project. Cheque-dishonour prosecutions under s.138 of the Negotiable Instruments Act, 1881 sit alongside all of this in sheer volume.",
      "And there is a switch that turns the litigation off. Once a corporate debtor enters CIRP, s.14 of the Insolvency and Bankruptcy Code, 2016 declares a moratorium prohibiting the institution or continuation of suits, transfer of assets and recovery actions — so a solvency check on the defendant is part of assessing the claim, not an afterthought.",
    ],
    statutes: [
      {
        act: "The Limitation Act, 1963",
        section: "Art. 113",
        heading: "Residuary suit — any suit with no named article",
        plain:
          "Three years. Where no other article of the Schedule fits, a commercial claim lands here.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 116",
        heading: "Civil appeals under the Code of Civil Procedure, 1908",
        plain:
          "(a) Ninety days to a High Court; (b) thirty days to any other court. Certified-copy time is excluded under s.12(2).",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 123",
        heading: "Setting aside an ex parte decree",
        plain: "Thirty days.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 136",
        heading: "Execution of a decree",
        plain:
          "Twelve years — and s.5 condonation is expressly unavailable for execution applications under Order XXI CPC.",
        source: "limitation-act",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.73",
        heading: "Compensation for loss or damage caused by breach of contract",
        plain:
          "The party who suffers a breach is entitled to compensation for loss or damage that naturally arose from it.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.74",
        heading: "Compensation for breach where a penalty is stipulated",
        plain:
          "Where a sum is named as payable on breach, the aggrieved party gets reasonable compensation not exceeding that sum.",
        source: "statute-library",
      },
      {
        act: "The Specific Relief Act, 1963",
        section: "s.10",
        heading: "Specific performance in respect of contracts",
        plain:
          "Specific performance shall be enforced except where money is adequate relief or the contract cannot be enforced.",
        source: "statute-library",
      },
      {
        act: "The Negotiable Instruments Act, 1881",
        section: "s.138",
        heading: "Dishonour of cheque for insufficiency of funds",
        plain:
          "The drawer of a dishonoured cheque commits an offence punishable with imprisonment up to two years or fine up to twice the cheque amount.",
        source: "statute-library",
      },
      {
        act: "The Insolvency and Bankruptcy Code, 2016",
        section: "s.14",
        heading: "Moratorium",
        plain:
          "On commencement of CIRP the NCLT declares a moratorium prohibiting institution of suits, transfer of assets and recovery actions.",
        source: "statute-library",
      },
    ],
    relatedFeatureSlugs: [
      "court-research-corpus",
      "citation-graph",
      "legal-simulators",
      "matter-management",
      "vio-ask",
      "nexus-search",
      "nexus-ask",
      "version-history",
      "white-label",
    ],
    relatedToolHrefs: ["/tools/limitation-period-finder"],
    relatedLimitationSlugs: [
      "residuary-suit",
      "civil-appeals",
      "set-aside-ex-parte-decree",
      "restoration-of-dismissed-case",
      "execution-of-decree",
      "residuary-application",
    ],
    relatedTemplateSlugs: [],
    relatedSolutionHrefs: [
      "/solutions/advocates",
      "/solutions/law-firms",
      "/solutions/enterprise",
    ],
    relatedProductHrefs: ["/product/legal-ai"],
    relatedCalendarRegulators: [],
    faqs: [
      {
        q: "Can a court condone delay in filing a suit in India?",
        a: "No. Section 5 of the Limitation Act, 1963 permits condonation of delay only for appeals and applications, never for suits. Section 3 goes further and requires the court to dismiss a time-barred suit 'although limitation has not been set up as a defence'. A late suit is not a weak suit — it is not a suit at all. Section 5 also carves out applications under Order XXI CPC, so a late execution application cannot be condoned either.",
      },
      {
        q: "What resets a limitation period once it has started?",
        a: "Two things principally: a written acknowledgment of liability signed by the party under s.18, and a part-payment acknowledged in the payer's handwriting or signed writing under s.19. Both give a fresh period, but only if made BEFORE the current period expires — acknowledging an already-barred debt creates no fresh period under the Act. Separately, ss.12-15 and s.17 exclude specific stretches of time (certified-copy time, stay periods, proceedings in a court without jurisdiction, and time before fraud or mistake is discovered).",
      },
      {
        q: "Does LexVio give litigation advice or predict case outcomes?",
        a: "No. LexVio ships a research corpus of judgments from the Supreme Court, High Courts, NCLT, ITAT, CCI, DRT and CESTAT, a citation graph that traces how cases cite each other, and 'legal simulators' — what-if engines for outcome odds, damages, settlement and cheque-bounce scenarios. Those are analytical aids that show their working and their sources. They are not advice, and no simulator output is a prediction of what a court will do on your facts.",
      },
      {
        q: "Where do the limitation periods on this site come from?",
        a: "From the India Code bare-Act PDF of the Limitation Act, 1963, text-extracted locally rather than paraphrased from commentary — including the Act's own printing errors, preserved with bracketed corrections. The Limitation Period Finder carries 64 Schedule articles plus four deadlines that live in other statutes (Arbitration Act s.34(3), NI Act ss.138/142, IBC s.61(2) and Consumer Protection Act s.69), and it names the articles it deliberately leaves out.",
      },
    ],
  },

  // ── 2 ─────────────────────────────────────────────────────────────────────
  {
    slug: "corporate-commercial",
    title: "Corporate & Commercial",
    short: "Corporate & Commercial",
    blurb:
      "Running an Indian company is a filing calendar attached to a governance code. The Companies Act, 2013 fixes the AGM under s.96(1), the annual return under s.92, financial statements under s.129 and s.137(1), director duties under s.166, interest disclosure under s.184 and related-party approvals under s.188 — and s.248 lets the Registrar strike the company off when it goes quiet. This hub links the governing sections to the nine ROC filings you actually have to diarise.",
    covers: [
      "Corporate and commercial work in India is bounded by the Companies Act, 2013 and, for LLPs, the LLP Act, 2008. The governance spine is short: directors owe the duties in s.166 (good faith, due care, no conflict, no undue gain), must disclose their interest under s.184 at the first Board meeting and annually, and must route related-party transactions through the consents s.188 requires. Auditors carry their own statutory duties under s.143, including the fraud-reporting obligation in s.143(12).",
      "Everything else is a deadline. Section 96(1) fixes the AGM; s.137(1) with Rule 12 of the Companies (Accounts) Rules 2014 drives AOC-4; s.92(4) with Rule 11 drives MGT-7/MGT-7A; s.139(1) with Rule 4(2) drives ADT-1; s.405 with the Specified Companies Order 2019 drives MSME-1 twice a year; Rule 16 of the Deposits Rules read with s.73 drives DPT-3; and Rule 12A of the Directors Rules drives DIR-3 KYC. LLPs run a parallel set — s.35(1) with Rule 25(1) for Form 11, s.34(2)/(3) with Rule 24 for Form 8.",
      "Commercial contracting sits on top: the same company also signs MSAs, vendor agreements and SaaS subscriptions, and those clauses are where liability actually accumulates. Section 129 requires financial statements that give a true and fair view under Schedule III, so a contingent liability buried in an indemnity clause eventually surfaces in the accounts.",
    ],
    statutes: [
      {
        act: "The Companies Act, 2013",
        section: "s.96(1)",
        heading: "Annual General Meeting",
        plain:
          "Every company other than a One Person Company must hold an AGM — the anchor date from which several ROC deadlines are counted.",
        source: "compliance-calendar",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.92",
        heading: "Annual return",
        plain:
          "Every company must file an annual return with the Registrar within 60 days of the AGM (MGT-7, or MGT-7A for small companies and OPCs).",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.137(1)",
        heading: "Copy of financial statement to be filed with the Registrar",
        plain:
          "Read with Rule 12 of the Companies (Accounts) Rules 2014 — the AOC-4 filing obligation.",
        source: "compliance-calendar",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.129",
        heading: "Financial statement",
        plain:
          "Financial statements must give a true and fair view, comply with Schedule III and follow the accounting standards under s.133.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.143",
        heading: "Powers and duties of auditors and auditing standards",
        plain:
          "Auditor powers, duties and reporting obligations, including fraud reporting under sub-section (12).",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.166",
        heading: "Duties of directors",
        plain:
          "Directors must act in good faith, exercise due care, avoid conflicts of interest and not achieve undue gain.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.184",
        heading: "Disclosure of interest by director",
        plain:
          "Every director must disclose concern or interest in any company, body corporate or firm at the first Board meeting and annually.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.188",
        heading: "Related party transactions",
        plain:
          "Prescribes prior Board consent and, above thresholds, shareholder approval for transactions with related parties.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.405",
        heading: "Power of Central Government to direct companies to furnish information",
        plain:
          "Read with the Specified Companies Order 2019 — the half-yearly MSME-1 return of dues outstanding beyond 45 days to micro and small suppliers.",
        source: "compliance-calendar",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.248",
        heading: "Power of Registrar to remove name of company from register",
        plain:
          "The Registrar may strike off a company that has not commenced business or is inoperative.",
        source: "statute-library",
      },
      {
        act: "The Limited Liability Partnership Act, 2008",
        section: "ss.34(2)-(3), 35(1)",
        heading: "Statement of account and solvency; annual return",
        plain:
          "Read with Rules 24 and 25(1) of the LLP Rules 2009 — LLP Form 8 and LLP Form 11 respectively.",
        source: "compliance-calendar",
      },
    ],
    relatedFeatureSlugs: [
      "filing-calendar",
      "regulator-monitoring",
      "regulatory-change-feed",
      "contract-templates",
      "contract-scanner",
      "matter-management",
      "nexus-search",
      "audit-log",
      "prebuilt-agents",
      "scheduled-runs",
      "white-label",
    ],
    relatedToolHrefs: [
      "/tools/roc-forms",
      "/calendar",
      "/library",
      "/tools/gstin-validator",
    ],
    relatedLimitationSlugs: ["residuary-application", "residuary-suit"],
    relatedTemplateSlugs: [
      "shareholders-agreement-india",
      "share-subscription-agreement-india",
      "founders-agreement-india",
      "esop-plan-and-grant-letter-india",
      "safe-post-money-india",
    ],
    relatedSolutionHrefs: [
      "/solutions/founders",
      "/solutions/ca-cs",
      "/solutions/smb",
      "/solutions/enterprise",
      "/solutions/law-firms",
    ],
    relatedProductHrefs: [
      "/product/compliance-ai",
      "/product/legal-ai",
      "/product/workflow-agents",
    ],
    relatedCalendarRegulators: ["mca", "llp", "sebi"],
    faqs: [
      {
        q: "Which MCA/ROC filings does a private limited company actually have to diarise?",
        a: "Nine, on the ROC Form Finder: AOC-4 (financial statements), MGT-7 or MGT-7A (annual return), ADT-1 (auditor appointment), DPT-3 (return of deposits), MSME-1 twice a year (dues outstanding beyond 45 days to micro and small enterprises), DIR-3 KYC for directors, and — for LLPs — Form 11 and Form 8. Each carries its own due rule and late-fee ladder, and the AGM under s.96(1) is the anchor several of them count from.",
      },
      {
        q: "Do statutory due dates move when they fall on a Sunday or a holiday?",
        a: "Not automatically. The calendar encodes the statutory date as-is. MCA V3, the GST portal, the e-filing portal, EPFO, ESIC and FLAIR accept filings 24x7, so regulators treat the calendar date as binding and grant relief only by ad-hoc notification. Section 10 of the General Clauses Act 1897 (the next-working-day rule) helps for physical filings with offices and courts, but should not be relied on for online tax payments.",
      },
      {
        q: "What does LexVio do for corporate secretarial work specifically?",
        a: "It monitors SEBI, RBI, MCA/ROC and GST circulars and filings end-to-end, keeps a filing calendar of every deadline that applies to your entity with advance alerts, and summarises new circulars into a change feed filterable by Act, regulator and effective date. On the document side it reads the contracts the company signs and groups them, with compliance items and research, by matter.",
      },
      {
        q: "Are the free contract templates safe to use for a company?",
        a: "They are drafting starting points, licensed free to copy, modify and use commercially without attribution — SAFE, SHA, SSA, ESOP plan and grant letter, founders' agreement, an India-compliant employment agreement and a DPDP data processing agreement. They are not legal advice, and stamp duty, registration and several employment obligations vary by state. Have a qualified advocate or company secretary review any of them before you sign.",
      },
    ],
  },

  // ── 3 ─────────────────────────────────────────────────────────────────────
  {
    slug: "contracts",
    title: "Contracts",
    short: "Contracts",
    blurb:
      "Every Indian contract stands or falls on the same handful of sections: s.10 for formation, s.23 for lawful object, s.27 which voids restraint of trade, s.28 which voids clauses that shorten your time to sue, s.55 on time being of the essence, and ss.73-74 on what a breach is actually worth. LexVio scores each clause against a benchmarked corpus of 22 market-standard clauses across SaaS, NDA, MSA, employment and vendor agreements, then hands back tracked-change Word output.",
    covers: [
      "Contract law in India is the Indian Contract Act, 1872 plus the drafting habits that have grown around it. Section 10 sets the four requirements — free consent, competent parties, lawful consideration, lawful object. Section 17 defines fraud, which is what a misrepresentation claim is built on. Section 23 makes consideration or object unlawful where it is forbidden by law, defeats a provision of law, is fraudulent, injures person or property, or is immoral or against public policy.",
      "Three sections quietly decide most negotiations. Section 27 voids every agreement that restrains anyone from exercising a lawful profession, trade or business, save for the sale of goodwill — which is why post-termination non-competes in Indian employment contracts fail and non-solicits are drafted narrowly instead. Section 28 voids clauses that restrict a party from enforcing rights through legal proceedings or that limit the time within which they may do so, so a 'claims must be brought within 90 days' clause is not enforceable simply because both sides signed it. Section 74 caps a stipulated penalty at reasonable compensation not exceeding the amount named, which is why an eye-watering liquidated-damages figure is rarely the win it looks like.",
      "LexVio's benchmarking corpus is India-first and deliberately narrow: 22 curated market-standard clauses across SaaS, NDA, MSA, employment and vendor agreements, covering 18 clause types — limitation of liability, indemnity, data privacy, termination, payment, IP ownership, confidentiality, non-compete, warranty, governing law, dispute resolution, force majeure, non-solicit, notice period, compensation, termination grounds, audit rights and delivery. A clause you paste is embedded, matched to the nearest standards, and returned with an above-market / at-market / below-market read.",
    ],
    statutes: [
      {
        act: "The Indian Contract Act, 1872",
        section: "s.10",
        heading: "What agreements are contracts",
        plain:
          "An agreement is a contract if made by free consent of competent parties, for lawful consideration and with a lawful object.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.17",
        heading: "Fraud defined",
        plain:
          "Suggestion of a fact known to be false, active concealment, or a promise made without intent to perform — committed with intent to deceive.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.23",
        heading: "What considerations and objects are lawful",
        plain:
          "Unlawful if forbidden by law, if it defeats a provision of law, is fraudulent, injures person or property, or is immoral or against public policy.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.27",
        heading: "Agreement in restraint of trade, void",
        plain:
          "Every agreement restraining anyone from exercising a lawful profession, trade or business is void, except on sale of goodwill.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.28",
        heading: "Agreements in restraint of legal proceedings, void",
        plain:
          "Clauses that restrict enforcement of rights through legal proceedings, or that shorten the time to bring them, are void.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.37",
        heading: "Obligation of parties to contracts",
        plain:
          "Parties must perform their promises unless performance is dispensed with or excused under the Act.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.55",
        heading: "Effect of failure to perform at a fixed time",
        plain:
          "Where time is of the essence, failure to perform within it makes the contract voidable at the promisee's option.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "ss.73-74",
        heading: "Compensation for breach; compensation where a penalty is stipulated",
        plain:
          "Damages for loss naturally arising from the breach; where a sum is named, reasonable compensation not exceeding that sum.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.124",
        heading: "Contract of indemnity defined",
        plain:
          "A promise to save the other party from loss caused by the promisor's conduct or by any other person.",
        source: "statute-library",
      },
      {
        act: "The Specific Relief Act, 1963",
        section: "s.14",
        heading: "Contracts not specifically enforceable",
        plain:
          "Lists what a court will not specifically enforce — including contracts compensable in money, dependent on personal qualifications, or requiring continuous supervision.",
        source: "statute-library",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 55",
        heading: "Compensation for breach of contract (residuary)",
        plain:
          "Three years, from when the contract is broken — or, where breach is recurring, when it ceases.",
        source: "limitation-act",
      },
    ],
    relatedFeatureSlugs: [
      "contract-scanner",
      "clause-risk-scoring",
      "legal-health-score",
      "fix-suggestions",
      "ai-redlining",
      "contract-templates",
      "bulk-analysis",
      "vio-ask",
      "vio-draft",
      "nexus-clause-coverage",
      "nexus-negotiation-rollup",
      "public-scanner",
      "legal-simulators",
      "version-history",
    ],
    relatedToolHrefs: [
      "/tools/nda-generator",
      "/library",
      "/tools/stamp-duty-calculator",
      "/tools/limitation-period-finder",
      "/tools/tds-rate-finder",
    ],
    relatedLimitationSlugs: [
      "breach-of-contract-general",
      "specific-performance-of-contract",
      "price-of-goods-sold-no-credit-period",
      "price-of-work-done",
      "cancellation-of-instrument-or-rescission",
    ],
    relatedTemplateSlugs: [
      "safe-post-money-india",
      "shareholders-agreement-india",
      "share-subscription-agreement-india",
      "esop-plan-and-grant-letter-india",
      "founders-agreement-india",
      "data-processing-agreement-dpdp-india",
      "employment-agreement-india",
    ],
    relatedSolutionHrefs: [
      "/solutions/founders",
      "/solutions/freelancers",
      "/solutions/agencies",
      "/solutions/smb",
      "/solutions/law-firms",
      "/solutions/enterprise",
    ],
    relatedProductHrefs: ["/product/legal-ai"],
    relatedCalendarRegulators: [],
    faqs: [
      {
        q: "Is a post-termination non-compete enforceable in India?",
        a: "As a general matter, no. Section 27 of the Indian Contract Act, 1872 voids every agreement that restrains a person from exercising a lawful profession, trade or business, with a narrow exception for the sale of goodwill. Indian drafting therefore leans on non-solicitation and confidentiality obligations rather than on a blanket non-compete. LexVio's benchmarked corpus carries the market non-solicit for employment agreements and the market non-compete used in NDAs, so you can see the shape counterparties actually accept.",
      },
      {
        q: "Can a contract shorten the time limit for bringing a claim?",
        a: "Section 28 of the Indian Contract Act, 1872 voids agreements that restrict a party from enforcing rights through legal proceedings or that limit the time within which they may do so. A contractual 'claims must be raised within X days' clause therefore cannot manufacture a limitation period shorter than the statute allows, however clearly it is drafted. The statutory period for a residuary breach-of-contract claim is three years under Article 55 of the Limitation Act, 1963.",
      },
      {
        q: "What does 'market standard' mean in LexVio's clause benchmarking?",
        a: "It means a curated, India-first corpus rather than an opinion. Twenty-two clauses across five contract types — SaaS, NDA, MSA, employment and vendor — each with the reasoning for why that position is the canonical middle ground. The canonical India SaaS liability cap in the corpus, for example, is twelve months' fees with carve-outs for indemnity, confidentiality, IP infringement, DPDP data-protection breach, and gross negligence, wilful misconduct or fraud. Your clause is embedded, matched to the nearest standards, and returned with an above-market, at-market or below-market verdict.",
      },
      {
        q: "Does LexVio produce a redlined Word file I can send to the counterparty?",
        a: "Yes. AI redlining produces a tracked-change Word document, and fix suggestions supply replacement language for each flagged clause rather than only naming the problem. Every version is retained so any two can be diffed side by side.",
      },
    ],
  },

  // ── 4 ─────────────────────────────────────────────────────────────────────
  {
    slug: "employment",
    title: "Employment",
    short: "Employment",
    blurb:
      "Indian employment work now straddles two regimes: the labour codes (Social Security 2020, Wages 2019, OSH 2020) with the old scheme provisions saved during transition, and the Indian Contract Act, 1872 whose s.27 voids the post-termination non-compete that half of all Indian offer letters still contain. The recurring obligations are relentless — EPF and ESI monthly, ESI half-yearly returns, the POSH annual report, statutory bonus, and salary TDS under s.192.",
    covers: [
      "The employment relationship in India is governed twice over. Contractually it sits under the Indian Contract Act, 1872 — which is why s.27 matters so much: a post-termination non-compete is void as a restraint of trade, so the enforceable protections are confidentiality, IP assignment and a narrowly drawn non-solicit. LexVio's benchmarked corpus carries market-standard employment clauses for exactly those points, plus notice period, compensation, data privacy and termination grounds.",
      "Statutorily it sits under the labour codes and their saved schemes. Section 16 of the Code on Social Security, 2020 (with the EPF Scheme 1952 provisions saved during transition) drives the monthly EPF contribution and ECR for establishments with 20+ employees. Section 29, read with Regulation 31 of the ESI (General) Regulations 1950, drives the monthly ESI contribution, and Regulation 26 drives the half-yearly returns of contributions for each of the April-September and October-March periods. Section 39 of the Code on Wages, 2019 — successor to s.19 of the Payment of Bonus Act, 1965 — sets annual statutory bonus disbursal.",
      "Two obligations are easy to forget and expensive to miss. Sections 21-22 of the Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013, read with Rule 14 of the POSH Rules 2013, require an annual report to the District Officer from every workplace with 10 or more employees, where an Internal Committee is mandatory. And the employer is a tax deductor: s.192 of the Income-tax Act, 1961 requires deduction at the average rate on estimated salary income at the time of payment, with the deposit and quarterly statement deadlines that follow.",
    ],
    statutes: [
      {
        act: "The Indian Contract Act, 1872",
        section: "s.27",
        heading: "Agreement in restraint of trade, void",
        plain:
          "Voids the post-termination non-compete; confidentiality, IP assignment and a narrow non-solicit are what remain enforceable.",
        source: "statute-library",
      },
      {
        act: "The Code on Social Security, 2020",
        section: "s.16",
        heading: "EPF — monthly contribution and ECR",
        plain:
          "Read with the SS (Central) Rules 2026; EPF Scheme 1952 provisions are saved during the transition. Applies to establishments with 20+ employees.",
        source: "compliance-calendar",
      },
      {
        act: "The Code on Social Security, 2020",
        section: "s.29",
        heading: "ESI — monthly contribution",
        plain:
          "Read with Regulation 31 of the ESI (General) Regulations 1950, saved during transition, and the SS (Central) Rules 2026.",
        source: "compliance-calendar",
      },
      {
        act: "The ESI (General) Regulations, 1950",
        section: "Reg. 26",
        heading: "Half-yearly return of contributions",
        plain:
          "Two returns a year — April-September and October-March — for all ESI-covered employers, saved under the SS Code 2020 transition.",
        source: "compliance-calendar",
      },
      {
        act: "The Code on Wages, 2019",
        section: "s.39",
        heading: "Statutory bonus — annual disbursal",
        plain: "Successor to s.19 of the Payment of Bonus Act, 1965.",
        source: "compliance-calendar",
      },
      {
        act: "The Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013",
        section: "ss.21-22",
        heading: "Annual report to the District Officer",
        plain:
          "Read with Rule 14 of the POSH Rules 2013. Every workplace with 10+ employees must constitute an Internal Committee and report annually.",
        source: "compliance-calendar",
      },
      {
        act: "The Occupational Safety, Health and Working Conditions Code, 2020",
        section: "Annual return",
        heading: "Unified electronic annual return (ex-CLRA Form XXV)",
        plain:
          "Read with the OSH (Central) Rules 2026. The calendar marks the exact rule and form number as still to be confirmed.",
        source: "compliance-calendar",
      },
      {
        act: "The Income-tax Act, 1961",
        section: "s.192",
        heading: "TDS on salary",
        plain:
          "The employer must deduct tax at the average rate on estimated salary income at the time of payment. Now s.392(1) of the Income-tax Act, 2025 from 1 April 2026.",
        source: "statute-library",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 7",
        heading: "Unpaid wages",
        plain: "Three years.",
        source: "limitation-act",
      },
    ],
    relatedFeatureSlugs: [
      "contract-scanner",
      "clause-risk-scoring",
      "contract-templates",
      "fix-suggestions",
      "ai-redlining",
      "filing-calendar",
      "bulk-analysis",
      "nexus-clause-coverage",
      "vio-draft",
      "tds-detection",
    ],
    relatedToolHrefs: [
      "/tools/nda-generator",
      "/tools/tds-rate-finder",
      "/calendar",
      "/library",
      "/tools/income-tax-slabs",
    ],
    relatedLimitationSlugs: ["recovery-of-unpaid-wages"],
    relatedTemplateSlugs: [
      "employment-agreement-india",
      "esop-plan-and-grant-letter-india",
    ],
    relatedSolutionHrefs: [
      "/solutions/smb",
      "/solutions/founders",
      "/solutions/ca-cs",
      "/solutions/enterprise",
    ],
    relatedProductHrefs: [
      "/product/legal-ai",
      "/product/compliance-ai",
      "/product/tax-ai",
    ],
    relatedCalendarRegulators: ["epf_esi", "labour", "income_tax"],
    faqs: [
      {
        q: "Which recurring employment filings does the compliance calendar track?",
        a: "The monthly EPF contribution and ECR under s.16 of the Code on Social Security, 2020; the monthly ESI contribution under s.29; the two ESI half-yearly returns of contributions under Regulation 26 of the ESI (General) Regulations 1950; the POSH annual report to the District Officer under ss.21-22 of the POSH Act, 2013 with Rule 14; annual statutory bonus disbursal under s.39 of the Code on Wages, 2019; and the unified annual return under the OSH Code, 2020. Salary TDS deposit and the quarterly TDS statements sit alongside them under the income-tax head.",
      },
      {
        q: "What does the free employment agreement template actually cover?",
        a: "It is an India-compliant employment agreement aligned to the labour codes, with confidentiality, IP assignment and a non-solicit drafted specifically against s.27 of the Indian Contract Act, 1872 — that is, drafted to survive the restraint-of-trade problem rather than to ignore it. Like every template in the library it is free to copy, modify and use commercially without attribution, and it is a drafting starting point, not advice.",
      },
      {
        q: "Does LexVio handle payroll or HRMS?",
        a: "No. LexVio reads and scores the documents — employment agreements, offer letters, ESOP plans and grant letters, vendor and contractor agreements — flags TDS obligations arising from their clauses, and tracks the statutory filing calendar. It is not a payroll engine and does not run your ECR or challans.",
      },
      {
        q: "Is a stock option grant taxed in India at grant or at exercise?",
        a: "The free ESOP Plan and Grant Letter template is aligned to the Companies Act, 2013 and the Share Capital and Debentures Rules, and states the Indian perquisite-tax position at exercise. Treat that as the template's own statement of the position and confirm it against current law for your facts — LexVio's tax analysis works section-wise against the Income-tax Act with CBDT circulars, and the section mapper shows where each 1961 provision lands in the Income-tax Act, 2025.",
      },
    ],
  },

  // ── 5 ─────────────────────────────────────────────────────────────────────
  {
    slug: "intellectual-property",
    title: "Intellectual Property",
    short: "Intellectual Property",
    blurb:
      "IP work in an Indian commercial practice is mostly contractual: who owns what the contractor built, what survives termination, and whether the licence grant is broad enough for the product you shipped. LexVio's benchmarked corpus carries market-standard IP-ownership clauses for both SaaS and employment agreements, the NDA generator ships an IP-heavy template, and the seeded statute library covers the Trade Marks Act, 1999 for the registration and infringement tests.",
    covers: [
      "Two of the three IP questions in a normal Indian commercial matter are answered by the contract, not the statute. Who owns work product created by an employee or a contractor is settled by the IP-assignment clause; what the customer may do with the vendor's platform, feedback and deliverables is settled by the IP-ownership and licence clauses. LexVio's benchmarking corpus carries market-standard IP-ownership positions for SaaS subscription agreements and for employment agreements, and the free employment agreement and founders' agreement templates both include IP assignment.",
      "The third question — is this mark registrable, and is that use an infringement — is statutory. Section 11 of the Trade Marks Act, 1999 sets the relative grounds for refusal: a mark cannot be registered where it is identical or similar to an earlier trade mark and the goods or services are identical or similar. Section 29 defines infringement as use, in the course of trade, of a mark identical or deceptively similar to a registered trade mark. For copyright, Article 88 of the Limitation Act, 1963 gives three years to sue for compensation for infringement.",
    ],
    statutes: [
      {
        act: "The Trade Marks Act, 1999",
        section: "s.11",
        heading: "Relative grounds for refusal of registration",
        plain:
          "A mark cannot be registered where it is identical or similar to an earlier trade mark and the goods or services are identical or similar.",
        source: "statute-library",
      },
      {
        act: "The Trade Marks Act, 1999",
        section: "s.29",
        heading: "Infringement of registered trade marks",
        plain:
          "Use in the course of trade of a mark identical or deceptively similar to a registered trade mark constitutes infringement.",
        source: "statute-library",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 88",
        heading: "Compensation for infringing copyright or any other exclusive privilege",
        plain: "Three years, from the date of the infringement.",
        source: "limitation-act",
      },
    ],
    relatedFeatureSlugs: [
      "contract-scanner",
      "clause-risk-scoring",
      "nexus-clause-coverage",
      "court-research-corpus",
      "citation-graph",
      "vio-ask",
      "contract-templates",
      "fix-suggestions",
    ],
    relatedToolHrefs: ["/tools/nda-generator", "/library"],
    relatedLimitationSlugs: ["copyright-infringement-compensation"],
    relatedTemplateSlugs: [
      "employment-agreement-india",
      "founders-agreement-india",
    ],
    relatedSolutionHrefs: [
      "/solutions/founders",
      "/solutions/agencies",
      "/solutions/advocates",
      "/solutions/law-firms",
    ],
    relatedProductHrefs: ["/product/legal-ai"],
    relatedCalendarRegulators: [],
    sparseNote:
      "This is one of the thinner hubs on the site, and deliberately so. LexVio's seeded statute library contains exactly one IP statute — the Trade Marks Act, 1999. The Patents Act, 1970, the Copyright Act, 1957 and the Designs Act, 2000 are not in it, so no section of theirs is cited here. What LexVio does carry for IP is contractual: benchmarked IP-ownership clauses for SaaS and employment agreements, an IP-heavy NDA template, and IP assignment inside the free employment and founders' agreements.",
    faqs: [
      {
        q: "Does LexVio do trade mark searches or patent prosecution?",
        a: "No. It does not file, prosecute or search registries. What it does is read the documents around IP — assignment and licence clauses in contracts, IP-ownership positions benchmarked against a market-standard corpus for SaaS and employment agreements — and give you the Indian court research corpus and citation graph for the case law around ss.11 and 29 of the Trade Marks Act, 1999.",
      },
      {
        q: "How do I make sure a contractor's work product belongs to my company?",
        a: "By an express assignment clause, not by assumption. LexVio's clause-coverage map shows which clauses are present, missing or non-standard across a portfolio, so a set of contractor agreements with no IP-assignment clause surfaces as a gap rather than as a surprise two years later. The free founders' agreement and employment agreement templates both include IP assignment as drafted starting points.",
      },
      {
        q: "Which NDA template should I use for an IP-sensitive discussion?",
        a: "The NDA generator ships five Indian-law templates — mutual, one-way, employment, vendor and IP-heavy. The IP-heavy variant is the one built for discussions where the disclosure itself is the asset. It runs in your browser and downloads as text or prints to PDF; nothing is uploaded.",
      },
    ],
  },

  // ── 6 ─────────────────────────────────────────────────────────────────────
  {
    slug: "compliance",
    title: "Compliance",
    short: "Compliance",
    blurb:
      "Indian statutory compliance is 49 live recurring obligations across nine regulators — GST, income-tax, MCA/ROC, LLP, EPF/ESI, labour, RBI, FEMA and SEBI — each with its own citation, applicability test and penalty ladder. The dates are statutory and do not shift for Sundays; regulators extend only by ad-hoc notification. This hub maps the governing provisions to the calendar, the ROC forms and the DPDP readiness assessment.",
    covers: [
      "Compliance in India is not one calendar but nine, stacked. GST alone runs GSTR-1 under s.37(1) of the CGST Act, 2017 with Rule 59(1), GSTR-3B under s.39(1) with Rule 61(1), the annual GSTR-9 under s.44 with Rule 80(1), and the GSTR-9C reconciliation under Rule 80(3) — plus CMP-08 and GSTR-4 for composition dealers, GSTR-7 for TDS deductors under s.51, GSTR-8 for e-commerce TCS under s.52, and ITC-04 for job work under s.143.",
      "Income-tax adds monthly TDS/TCS deposit under s.397 of the Income-tax Act, 2025 with Rule 218 of the Income-tax Rules 2026, four quarterly statements under s.397(3)(b), four advance-tax instalments under s.408, the return under s.263(1), the tax-audit report under s.63, and the transfer-pricing report under s.172. MCA adds AGM, AOC-4, MGT-7/7A, ADT-1, DPT-3, MSME-1 and DIR-3 KYC; LLPs add Forms 11 and 8. EPF/ESI, labour, RBI's ECB-2, FEMA's FLA and ODI APR, and SEBI LODR quarterly and annual filings complete the set.",
      "Privacy is the newest layer and the one most organisations have mis-dated. The DPDP Act, 2023 commenced in phases under G.S.R. 843(E): 13 November 2025 stood up the Data Protection Board only, 13 November 2026 opens Consent Manager registration under s.6(9) and nothing else, and 13 May 2027 brings the entire operative regime — ss.3-5, s.6(1)-(8) and (10), ss.7-17, and the penalty machinery in ss.28-34 with the Schedule. The readiness quiz groups every gap it finds against those dates.",
    ],
    statutes: [
      {
        act: "The Central Goods and Services Tax Act, 2017",
        section: "s.37(1)",
        heading: "Furnishing details of outward supplies (GSTR-1)",
        plain:
          "Read with Rule 59(1) of the CGST Rules 2017; the 11th of the following month was fixed by Notification 83/2020-Central Tax.",
        source: "compliance-calendar",
      },
      {
        act: "The Central Goods and Services Tax Act, 2017",
        section: "s.39(1)",
        heading: "Furnishing of returns (GSTR-3B)",
        plain: "Read with Rule 61(1) of the CGST Rules 2017.",
        source: "compliance-calendar",
      },
      {
        act: "The Central Goods and Services Tax Act, 2017",
        section: "s.44",
        heading: "Annual return (GSTR-9 / GSTR-9C)",
        plain:
          "Read with Rules 80(1) and 80(3) — the annual return and the reconciliation statement respectively.",
        source: "compliance-calendar",
      },
      {
        act: "The Income-tax Act, 2025",
        section: "s.397",
        heading: "TDS/TCS deposit and quarterly statements",
        plain:
          "Read with Rule 218 for deposit and Rules 217/219 for the quarterly statements (Forms 138/140/143/144).",
        source: "compliance-calendar",
      },
      {
        act: "The Income-tax Act, 2025",
        section: "s.263(1)",
        heading: "Return of income",
        plain:
          "Successor of s.139(1) of the Income-tax Act, 1961. Sub-sections (4) and (5) carry the belated and revised return windows.",
        source: "compliance-calendar",
      },
      {
        act: "The Companies Act, 2013",
        section: "ss.92(4), 137(1), 139(1)",
        heading: "Annual return; financial statements; auditor appointment",
        plain:
          "Read with Rule 11 of the Management & Administration Rules, Rule 12 of the Accounts Rules and Rule 4(2) of the Audit and Auditors Rules — MGT-7, AOC-4 and ADT-1.",
        source: "compliance-calendar",
      },
      {
        act: "The Code on Social Security, 2020",
        section: "ss.16, 29",
        heading: "EPF and ESI monthly contributions",
        plain:
          "The EPF Scheme 1952 and ESI (General) Regulations 1950 provisions are saved during the transition to the Code.",
        source: "compliance-calendar",
      },
      {
        act: "The SEBI (Listing Obligations and Disclosure Requirements) Regulations, 2015",
        section: "Regs. 13(3), 27(2), 31(1)(b), 33(3)(d)",
        heading: "Quarterly filings and annual audited results",
        plain:
          "Read with the SEBI Integrated Filing framework. Listed entities only.",
        source: "compliance-calendar",
      },
      {
        act: "The Foreign Exchange Management Act, 1999",
        section: "FLA / ECB-2 / APR",
        heading: "Reporting under FEMA",
        plain:
          "The annual Foreign Liabilities & Assets return, the monthly ECB-2 return, and the Annual Performance Report under Regulation 10 of the FEM (Overseas Investment) Regulations 2022.",
        source: "compliance-calendar",
      },
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "ss.3-17, 28-34",
        heading: "The operative regime, in force from 13 May 2027",
        plain:
          "Application and extraterritorial reach, grounds for processing, notice, consent, Data Fiduciary obligations, children's data, Significant Data Fiduciaries, Data Principal rights, cross-border restriction, and the penalty machinery with the Schedule.",
        source: "dpdp-dataset",
      },
    ],
    relatedFeatureSlugs: [
      "filing-calendar",
      "regulator-monitoring",
      "regulatory-change-feed",
      "dpdp-readiness",
      "prebuilt-agents",
      "custom-agents",
      "scheduled-runs",
      "approval-workflows",
      "webhooks",
      "audit-log",
      "gst-computation",
      "tds-detection",
    ],
    relatedToolHrefs: [
      "/calendar",
      "/tools/roc-forms",
      "/tools/dpdp-readiness-quiz",
      "/tools/interest-calculator",
      "/tools/gstin-validator",
    ],
    relatedLimitationSlugs: [],
    relatedTemplateSlugs: ["data-processing-agreement-dpdp-india"],
    relatedSolutionHrefs: [
      "/solutions/ca-cs",
      "/solutions/smb",
      "/solutions/enterprise",
      "/solutions/financial-institutions",
      "/solutions/law-firms",
    ],
    relatedProductHrefs: [
      "/product/compliance-ai",
      "/product/workflow-agents",
      "/product/tax-ai",
    ],
    relatedCalendarRegulators: [
      "gst",
      "income_tax",
      "mca",
      "llp",
      "epf_esi",
      "labour",
      "rbi",
      "fema",
      "sebi",
    ],
    faqs: [
      {
        q: "How many statutory deadlines does an Indian company actually have?",
        a: "The compliance calendar carries 49 live recurring obligations across nine regulators, but almost none of them apply to every entity — each row states who it applies to. A private company with no listing, no ECB, no FDI, no composition scheme and fewer than 20 employees will match a small fraction of them. The applicability line is the part to read first.",
      },
      {
        q: "When does the DPDP Act actually start biting?",
        a: "13 May 2027 for substantive compliance and enforcement. The commencement notification G.S.R. 843(E) split the Act into three phases: 13 November 2025 brought only the Data Protection Board provisions into force — the institution, not the jurisdiction, and s.27's complaint power was not among them. 13 November 2026 brings only s.6(9) and s.27(1)(d), which concern Consent Manager registration. Everything else — notice, consent, security safeguards, breach intimation, children's data, Data Principal rights, cross-border, and the penalty Schedule — commences at eighteen months, on 13 May 2027.",
      },
      {
        q: "Do the calendar's dates account for extensions?",
        a: "No, and that is deliberate. The dates encoded are the statutory dates. Regulators extend ad hoc by notification — Notification 01/2026-Central Tax moved the March-2026 GSTR-3B from 20 to 21 April 2026, and CBDT moved the AY 2025-26 tax-audit specified date from 30 September to 31 October 2025 — and encoding those retroactively would make the calendar unreliable in the other direction. Track extension notifications separately; use the calendar for the date to work backwards from.",
      },
      {
        q: "What can LexVio automate here rather than just track?",
        a: "Pre-built agents cover vendor-onboarding review, a weekly compliance scan and GST reconciliation. Custom agents let you build a multi-step workflow with triggers, conditions and actions without code, and approval workflows put a human gate between agent steps. Scheduled runs are cron-style — daily, weekly, or on the GST due date — and webhooks fire outward on scan-complete, alert-fired and deadline-hit.",
      },
    ],
  },

  // ── 7 ─────────────────────────────────────────────────────────────────────
  {
    slug: "tax-regulatory",
    title: "Tax & Regulatory",
    short: "Tax & Regulatory",
    blurb:
      "Indian tax practice is mid-transition: the Income-tax Act, 1961 governs income up to 31 March 2026 and the Income-tax Act, 2025 takes over from 1 April 2026, collapsing 819 sections into 536 and folding most TDS obligations into table serials inside s.393. GST runs in parallel under the CGST Act, 2017. This hub carries the section mapper, the TDS finder, the slab tables, the CII series and the interest calculator, each citing its own source.",
    covers: [
      "The single most consequential fact in Indian tax right now is the changeover. Income earned up to 31 March 2026 stays on the Income-tax Act, 1961; income from 1 April 2026 is Tax Year 2026-27 and runs on the Income-tax Act, 2025. Most TDS provisions no longer have standalone section numbers — s.194J professional fees becomes a serial in the s.393(1) table, s.194C contractor payments another, s.195 non-resident payments a serial in s.393(2). Citing the legacy number after the changeover is a drafting defect, not a shorthand.",
      "The thresholds moved too. The Finance Act 2025 raised the s.194J professional-fees threshold to ₹50,000 a year, s.194I rent to ₹6 lakh a year (₹50,000 a month), and s.194H commission and brokerage to ₹20,000 a year, all with effect from 1 April 2025. Section 194C's ₹30,000 single / ₹1 lakh aggregate thresholds were left unchanged. Section 194Q still applies to buyers with turnover above ₹10 crore purchasing goods above ₹50 lakh a year per seller.",
      "GST is the other half. Section 7 of the CGST Act, 2017 defines supply; s.9(3) and (4) carry reverse charge; s.16 sets the four conditions for input tax credit — possession of a tax invoice, receipt of goods or services, tax actually paid by the supplier, and the return furnished; and s.17(5) lists the blocked credits that catch people out. Section 31 requires the tax invoice whose fields Rule 46 prescribes. Capital gains carry their own trap: indexation was withdrawn for most transfers on or after 23 July 2024, which the Cost Inflation Index tool states on its face.",
    ],
    statutes: [
      {
        act: "The Income-tax Act, 1961",
        section: "s.194C",
        heading: "Payments to contractors",
        plain:
          "1% for individual/HUF payees, 2% for others, above ₹30,000 single / ₹1 lakh aggregate. Now s.393(1) Table Sl. No. 6(i) of the Income-tax Act, 2025.",
        source: "statute-library",
      },
      {
        act: "The Income-tax Act, 1961",
        section: "s.194J",
        heading: "Fees for professional or technical services",
        plain:
          "10% on professional fees, 2% on technical fees, above ₹50,000 a year after the Finance Act 2025. Now s.393(1) Table Sl. No. 6(iii).",
        source: "statute-library",
      },
      {
        act: "The Income-tax Act, 1961",
        section: "s.194I",
        heading: "Rent",
        plain:
          "10% on land and building, 2% on plant and machinery, above ₹6 lakh a year (₹50,000 a month) after the Finance Act 2025. Now s.393(1) Table Sl. No. 2(ii).",
        source: "statute-library",
      },
      {
        act: "The Income-tax Act, 1961",
        section: "s.195",
        heading: "Other sums — payments to non-residents",
        plain:
          "TDS on any sum chargeable to tax paid to a non-resident, at rates in force or the applicable DTAA rate, whichever is more beneficial. Now s.393(2) Table Sl. No. 17.",
        source: "statute-library",
      },
      {
        act: "The Income-tax Act, 1961",
        section: "s.211",
        heading: "Instalments of advance tax and due dates",
        plain:
          "15 June (15%), 15 September (45%), 15 December (75%), 15 March (100%). Now s.408 of the Income-tax Act, 2025 — dates and percentages unchanged.",
        source: "statute-library",
      },
      {
        act: "The Income-tax Act, 1961",
        section: "s.206C",
        heading: "Tax collected at source",
        plain:
          "Sellers of specified goods collect 0.1% to 5% at source. Now s.394 of the Income-tax Act, 2025; the individual serials inside the s.394(1) table are not yet confirmed.",
        source: "statute-library",
      },
      {
        act: "The Central Goods and Services Tax Act, 2017",
        section: "s.7",
        heading: "Scope of supply",
        plain:
          "Sale, transfer, barter, exchange, licence, rental, lease or disposal made or agreed to be made for a consideration.",
        source: "statute-library",
      },
      {
        act: "The Central Goods and Services Tax Act, 2017",
        section: "s.9(3)-(4)",
        heading: "Levy and collection — reverse charge",
        plain:
          "On notified categories of supply the recipient, not the supplier, pays the GST.",
        source: "statute-library",
      },
      {
        act: "The Central Goods and Services Tax Act, 2017",
        section: "s.16",
        heading: "Eligibility and conditions for taking input tax credit",
        plain:
          "Tax invoice in hand, goods or services received, tax paid by the supplier, and the return furnished — all four, or no credit.",
        source: "statute-library",
      },
      {
        act: "The Central Goods and Services Tax Act, 2017",
        section: "s.17(5)",
        heading: "Blocked credits",
        plain:
          "Motor vehicles (with exceptions), food and beverages, club memberships and the rest of the blocked list.",
        source: "statute-library",
      },
      {
        act: "The Central Goods and Services Tax Act, 2017",
        section: "s.31",
        heading: "Tax invoice",
        plain:
          "A registered person making a taxable supply must issue a tax invoice; the required fields are in Rule 46.",
        source: "statute-library",
      },
    ],
    relatedFeatureSlugs: [
      "income-tax-analysis",
      "tds-detection",
      "gst-computation",
      "ifrs-indas",
      "cross-border-tax",
      "filing-calendar",
      "regulator-monitoring",
      "regulatory-change-feed",
      "gst-compliant-billing",
      "prebuilt-agents",
      "gst-calculator-tool",
      "tds-rate-finder-tool",
      "hsn-sac-finder-tool",
      "gstin-validator-tool",
    ],
    relatedToolHrefs: [
      "/tools/income-tax-section-mapper",
      "/tools/tds-rate-finder",
      "/tools/gst-calculator",
      "/tools/hsn-sac-finder",
      "/tools/gstin-validator",
      "/tools/income-tax-slabs",
      "/tools/cost-inflation-index",
      "/tools/interest-calculator",
      "/calendar",
    ],
    relatedLimitationSlugs: [],
    relatedTemplateSlugs: [],
    relatedSolutionHrefs: [
      "/solutions/ca-cs",
      "/solutions/smb",
      "/solutions/founders",
      "/solutions/freelancers",
      "/solutions/enterprise",
    ],
    relatedProductHrefs: ["/product/tax-ai", "/product/compliance-ai", "/product/global"],
    relatedCalendarRegulators: ["income_tax", "gst", "fema", "rbi"],
    faqs: [
      {
        q: "Which Income-tax Act applies to my transaction?",
        a: "Income earned up to 31 March 2026 stays on the Income-tax Act, 1961. Income earned from 1 April 2026 is Tax Year 2026-27 and runs on the Income-tax Act, 2025. The section mapper carries 80 mappings across TDS, deductions, capital gains, business, compliance and international heads, each with a confidence badge — confirmed rows were checked against the bare text of the 2025 Act, and unverified rows are marked as gaps rather than guessed.",
      },
      {
        q: "Why can I not find s.194J in the Income-tax Act, 2025?",
        a: "Because most TDS obligations stopped being standalone sections. They are now table serials inside s.393 — professional and technical fees at s.393(1) Table Sl. No. 6(iii), contractor payments at Sl. No. 6(i), rent at Sl. No. 2(ii), commission and brokerage at Sl. No. 1(ii), purchase of goods at Sl. No. 8(ii), and non-resident payments at s.393(2) Table Sl. No. 17. After the changeover, cite the serial.",
      },
      {
        q: "Does LexVio compute my tax or file my returns?",
        a: "It does not file. The free tools compute — GST forward and reverse with the CGST/SGST or IGST split, TDS rates by section, slab tax under either regime, indexed cost from the notified CII series, and interest and late fees under 234A/234B/234C, CGST s.50, TDS 201(1A) and the ₹200-a-day 234E fee, each shown step by step with its citation. Inside the product, tax analysis reads your contracts section-wise against the Act with CBDT circulars and flags TDS obligations arising from clauses.",
      },
      {
        q: "Is indexation still available on capital gains?",
        a: "Not for most transfers on or after 23 July 2024 — indexation was withdrawn, and the Cost Inflation Index tool says so on its face rather than quietly computing a number you cannot use. The notified CII series is still published and still relevant to transfers before that date, from base 100 for FY 2001-02 through 384 for FY 2026-27.",
      },
    ],
  },

  // ── 8 ─────────────────────────────────────────────────────────────────────
  {
    slug: "dispute-resolution",
    title: "Dispute Resolution",
    short: "Dispute Resolution",
    blurb:
      "Arbitration, insolvency, consumer and cheque-bounce proceedings each run on a deadline the Limitation Act does not govern, and two of them are hard ceilings no court can lift: s.34(3) of the Arbitration and Conciliation Act, 1996 allows three months plus at most thirty days 'but not thereafter', and s.61(2) of the IBC caps a NCLAT appeal at thirty days plus fifteen. The clause that sends you there is drafted long before the dispute, which is where LexVio reads it.",
    covers: [
      "Dispute resolution begins in the contract. The governing-law and dispute-resolution clauses decide the forum, the seat and the escalation ladder, and LexVio's benchmarked corpus carries market-standard versions of both for MSAs. Section 28 of the Indian Contract Act, 1872 sets the outer limit on what those clauses can do: an agreement that restricts a party from enforcing rights through legal proceedings, or that limits the time within which they may do so, is void.",
      "Once a dispute is live, the special statutes take over from the Limitation Act Schedule. An application to set aside an arbitral award under s.34(3) of the Arbitration and Conciliation Act, 1996 must be made within three months, extendable by a maximum of thirty days on sufficient cause, 'but not thereafter'. An appeal to the NCLAT under s.61(2) of the IBC is thirty days, condonable by a further period not exceeding fifteen — a hard forty-five-day outer cap. A consumer complaint under s.69 of the Consumer Protection Act, 2019 is two years, with delay condonable on sufficient cause and reasons recorded.",
      "Insolvency itself is a dispute route. A financial creditor files under s.7 of the IBC and an operational creditor under s.9, after the s.8 demand notice and ten days without payment. Section 14 then declares the moratorium, and s.29A bars defaulters, wilful defaulters, undischarged insolvents and connected persons from submitting a resolution plan. Cheque dishonour under s.138 of the Negotiable Instruments Act, 1881 runs on its own chained clock: presentation within validity, demand notice within thirty days of the return memo, the drawer's fifteen-day payment window, then the complaint.",
    ],
    statutes: [
      {
        act: "The Arbitration and Conciliation Act, 1996",
        section: "s.34(3)",
        heading: "Application for setting aside an arbitral award",
        plain:
          "Three months, condonable by a maximum further thirty days on sufficient cause, 'but not thereafter' — a ceiling, not a guideline.",
        source: "limitation-act",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.28",
        heading: "Agreements in restraint of legal proceedings, void",
        plain:
          "Clauses restricting enforcement through legal proceedings, or shortening the time to bring them, are void.",
        source: "statute-library",
      },
      {
        act: "The Insolvency and Bankruptcy Code, 2016",
        section: "s.7",
        heading: "Initiation of CIRP by a financial creditor",
        plain:
          "A financial creditor may apply to the NCLT to initiate the corporate insolvency resolution process once default has occurred.",
        source: "statute-library",
      },
      {
        act: "The Insolvency and Bankruptcy Code, 2016",
        section: "s.9",
        heading: "Initiation of CIRP by an operational creditor",
        plain:
          "Available after a s.8 demand notice where no payment is received within ten days.",
        source: "statute-library",
      },
      {
        act: "The Insolvency and Bankruptcy Code, 2016",
        section: "s.29A",
        heading: "Persons not eligible to be a resolution applicant",
        plain:
          "Bars defaulters, undischarged insolvents, wilful defaulters, persons with NPA accounts and connected persons.",
        source: "statute-library",
      },
      {
        act: "The Insolvency and Bankruptcy Code, 2016",
        section: "s.61(2)",
        heading: "Appeal to the NCLAT",
        plain:
          "Thirty days, condonable for sufficient cause by a further period not exceeding fifteen days — a hard 45-day outer cap.",
        source: "limitation-act",
      },
      {
        act: "The Negotiable Instruments Act, 1881",
        section: "ss.138, 142",
        heading: "Dishonour of cheque; cognizance of offences",
        plain:
          "Chained deadlines — presentation within validity, written demand within thirty days of the return memo, the drawer's fifteen-day window, then the complaint.",
        source: "limitation-act",
      },
      {
        act: "The Consumer Protection Act, 2019",
        section: "s.69",
        heading: "Limitation period for a consumer complaint",
        plain:
          "Two years; delay condonable on sufficient cause, with the Commission required to record its reasons.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 137",
        heading: "Residuary application",
        plain:
          "Three years — where IBC applications and several tribunal applications are mapped by case law rather than by the Schedule's own words.",
        source: "limitation-act",
      },
    ],
    relatedFeatureSlugs: [
      "legal-simulators",
      "court-research-corpus",
      "citation-graph",
      "clause-risk-scoring",
      "contract-scanner",
      "matter-management",
      "nexus-search",
      "vio-ask",
      "nexus-clause-coverage",
    ],
    relatedToolHrefs: ["/tools/limitation-period-finder"],
    relatedLimitationSlugs: [
      "setting-aside-arbitral-award",
      "cheque-bounce-complaint-timeline",
      "ibc-appeal-to-nclat",
      "consumer-complaint-limitation",
      "residuary-application",
    ],
    relatedTemplateSlugs: [],
    relatedSolutionHrefs: [
      "/solutions/advocates",
      "/solutions/law-firms",
      "/solutions/enterprise",
      "/solutions/financial-institutions",
    ],
    relatedProductHrefs: ["/product/legal-ai"],
    relatedCalendarRegulators: [],
    faqs: [
      {
        q: "Can a court condone a late application to set aside an arbitral award?",
        a: "Only up to thirty days beyond the three-month period, and not a day more. Section 34(3) of the Arbitration and Conciliation Act, 1996 says the extension may be granted on sufficient cause 'but not thereafter'. That phrase is what makes it a ceiling rather than a discretion. The same architecture appears in s.61(2) of the IBC, where the fifteen-day extension produces a hard forty-five-day outer limit.",
      },
      {
        q: "Does the Limitation Act, 1963 govern these special-statute deadlines?",
        a: "Only as far as the special law allows. Under the s.29(2) scheme, a special law's own limitation regime governs and can exclude s.5 condonation expressly or by its scheme. The Arbitration Act's 'but not thereafter' and the IBC's fifteen-day cap operate as hard ceilings; the Consumer Protection Act builds its own condonation into s.69(2). The Schedule's rules do not simply carry over.",
      },
      {
        q: "What does LexVio's cheque-bounce simulator actually do?",
        a: "The legal simulators are what-if engines covering outcome odds, damages, settlement, cheque-bounce, tax scenarios and AI negotiation roleplay. For cheque bounce specifically, the value is in the chained deadlines under ss.138 and 142 of the Negotiable Instruments Act, 1881 — presentation, the thirty-day demand notice, the drawer's fifteen-day window, then the complaint — where a miss at any link ends the prosecution. The simulator is an analytical aid, not a prediction of what a court will do.",
      },
      {
        q: "Where should the dispute-resolution clause point?",
        a: "That is a commercial decision, not one this page can make. What LexVio can tell you is where your clause sits against the market: the benchmarked corpus carries a market-standard dispute-resolution clause and a market-standard governing-law clause for MSAs, and clause-level risk scoring flags a clause that is materially off-market with an explanation and a confidence score.",
      },
    ],
  },

  // ── 9 ─────────────────────────────────────────────────────────────────────
  {
    slug: "real-estate",
    title: "Real Estate",
    short: "Real Estate",
    blurb:
      "Indian property work is decided by three things a contract review must catch: whether the instrument is adequately stamped under the state's own schedule, whether the claim is inside its limitation article — thirty years to redeem a mortgage, twelve for possession on title, three for specific performance — and what the tax position is on transfer, where indexation was withdrawn for most transfers on or after 23 July 2024.",
    covers: [
      "Stamp duty in India is a state subject, and the spread is the point: a sale deed attracts around 6% in Maharashtra with an extra 1% LBT in Mumbai, and each state sets its own rates for gift deeds, leases over twelve months, loan and rent agreements and letters of authority, with female-buyer rebates in several. The stamp duty calculator carries 22 states and links each to its own Registration & Stamps department, because the department, not a calculator, is the authority before execution.",
      "The limitation articles for property are the longest on the books and the least forgiving. Article 61 gives thirty years to redeem a mortgage; Article 63 gives thirty years for a mortgagee's foreclosure or possession in one limb and twelve in the other; Article 62 gives twelve years to enforce payment of mortgage money; Article 65 gives twelve years for possession based on title, which is the article adverse possession is argued under; Article 67 gives twelve years to a landlord recovering possession from a tenant. Rent arrears run on three years under Article 52, trespass three years under Article 87, and specific performance of a sale agreement three years under Article 54.",
      "Two more provisions shape outcomes. Section 20A of the Specific Relief Act, 1963 bars a court from granting an injunction in a suit involving an infrastructure project where it would impede or delay progress. And on the tax side, s.194I of the Income-tax Act, 1961 requires 10% TDS on rent for land or building above ₹6 lakh a year after the Finance Act 2025 — now s.393(1) Table Sl. No. 2(ii) of the Income-tax Act, 2025 from 1 April 2026.",
    ],
    statutes: [
      {
        act: "The Limitation Act, 1963",
        section: "Art. 61",
        heading: "Mortgage redemption",
        plain:
          "(a) Thirty years to redeem or recover possession; (b) twelve years; (c) three years, depending on the limb.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 65",
        heading: "Possession of immovable property based on title",
        plain:
          "Twelve years, from when the defendant's possession becomes adverse to the plaintiff — the adverse-possession article.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 67",
        heading: "Landlord recovering possession from a tenant",
        plain: "Twelve years, from when the tenancy is determined.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 54",
        heading: "Specific performance of a contract",
        plain:
          "Three years — from the date fixed for performance, or when the plaintiff has notice that performance is refused.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 52",
        heading: "Arrears of rent",
        plain: "Three years.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 87",
        heading: "Trespass to immovable property",
        plain: "Three years.",
        source: "limitation-act",
      },
      {
        act: "The Specific Relief Act, 1963",
        section: "s.20A",
        heading: "Special provisions for contracts relating to infrastructure projects",
        plain:
          "A court shall not grant an injunction in a suit involving an infrastructure project where it would impede or delay progress.",
        source: "statute-library",
      },
      {
        act: "The Specific Relief Act, 1963",
        section: "ss.10, 14",
        heading: "Specific performance; contracts not specifically enforceable",
        plain:
          "Specific performance is enforced except where money is adequate relief or the contract falls in the s.14 list.",
        source: "statute-library",
      },
      {
        act: "The Income-tax Act, 1961",
        section: "s.194I",
        heading: "TDS on rent",
        plain:
          "10% on land or building, 2% on plant and machinery, above ₹6 lakh a year after the Finance Act 2025. Now s.393(1) Table Sl. No. 2(ii).",
        source: "statute-library",
      },
    ],
    relatedFeatureSlugs: [
      "contract-scanner",
      "clause-risk-scoring",
      "vio-ask",
      "fix-suggestions",
      "stamp-duty-calculator-tool",
      "tds-detection",
      "legal-health-score",
      "court-research-corpus",
    ],
    relatedToolHrefs: [
      "/tools/stamp-duty-calculator",
      "/tools/limitation-period-finder",
      "/tools/cost-inflation-index",
      "/tools/tds-rate-finder",
    ],
    relatedLimitationSlugs: [
      "mortgage-redemption",
      "possession-based-on-title-adverse-possession",
      "landlord-recovery-from-tenant",
      "specific-performance-of-contract",
      "arrears-of-rent",
      "trespass-to-immovable-property",
    ],
    relatedTemplateSlugs: [],
    relatedSolutionHrefs: [
      "/solutions/smb",
      "/solutions/advocates",
      "/solutions/law-firms",
    ],
    relatedProductHrefs: ["/product/legal-ai", "/product/tax-ai"],
    relatedCalendarRegulators: ["income_tax"],
    sparseNote:
      "There is no free template for a sale deed, lease or leave-and-licence agreement in the library, and none is implied here — the seven templates are startup, employment and privacy documents. LexVio's seeded statute library also does not carry the Transfer of Property Act, 1882, the Registration Act, 1908 or the Real Estate (Regulation and Development) Act, 2016, so no section of theirs is cited on this page. What this hub does carry is real: state-wise stamp duty, the property limitation articles quoted from the bare Act, and the transfer-tax tools.",
    faqs: [
      {
        q: "How much stamp duty will I pay on a sale deed?",
        a: "It depends entirely on the state, and often on the buyer. The calculator carries 22 states with their own rates for sale deeds, gift deeds, leases over twelve months, loan agreements, commercial rent agreements and letters of authority, along with female-buyer rebates where they exist and local surcharges such as the 1% LBT in Mumbai. It is a working estimate reviewed for FY 2025-26; confirm with the state's Registration & Stamps department before execution.",
      },
      {
        q: "How long do I have to sue for specific performance of a sale agreement?",
        a: "Three years under Article 54 of the Limitation Act, 1963, running from the date fixed for performance or, where no date is fixed, from when the plaintiff has notice that performance has been refused. Section 5 condonation is not available for suits, so the period is effectively absolute unless an exclusion under ss.12-15 or s.17 applies on your facts.",
      },
      {
        q: "Can I still claim indexation when I sell property?",
        a: "Not for most transfers on or after 23 July 2024 — indexation was withdrawn, and the Cost Inflation Index tool states that on its face. The notified CII series remains published and remains relevant to earlier transfers, from base 100 for FY 2001-02 through 384 for FY 2026-27, with an indexed-cost calculator alongside it.",
      },
      {
        q: "Does LexVio review lease and licence agreements?",
        a: "Yes — the contract scanner runs a three-tier extraction cascade so PDFs, DOCX files and scanned bilingual documents all parse, then scores every clause red, amber or green with an explanation and a confidence score, and produces a Legal Health Score out of 100 for the document as a whole. There is no property-specific module beyond that, and no title search.",
      },
    ],
  },

  // ── 10 ────────────────────────────────────────────────────────────────────
  {
    slug: "banking-finance",
    title: "Banking & Finance",
    short: "Banking & Finance",
    blurb:
      "Lending and financial-services work in India sits across four regulators at once: the RBI for NBFC registration and net owned fund under s.45IA of the RBI Act, 1934 and for ECB reporting; SEBI for market conduct under ss.12A, 15G and 15HA of the SEBI Act, 1992; the NCLT for recovery under ss.7 and 9 of the IBC; and FEMA for FLA and overseas-investment reporting. The recovery clocks are Limitation Act articles 19, 21, 62 and 63.",
    covers: [
      "The regulatory perimeter comes first. Section 45IA of the RBI Act, 1934 prohibits any NBFC from commencing or carrying on non-banking financial business without RBI registration and the minimum net owned fund, and s.17 lists the business the Bank itself may transact. Entities with outstanding external commercial borrowings file the monthly ECB-2 return under the FEMA reporting framework and the ECB Master Direction; entities with FDI or overseas investment file the annual FLA return, and holders of ODI file the Annual Performance Report under Regulation 10 of the FEM (Overseas Investment) Regulations 2022.",
      "Market conduct sits with SEBI. Section 11 of the SEBI Act, 1992 sets the Board's functions; s.12A prohibits manipulative and deceptive devices in connection with the issue, purchase or sale of any listed security; s.15G penalises insider trading at ₹25 crore or three times the profit, whichever is higher, and s.15HA does the same for fraudulent and unfair trade practices. Listed entities file quarterly and annual results under the SEBI LODR Regulations, 2015 through the Integrated Filing framework.",
      "Recovery runs on two tracks. Under the IBC, a financial creditor applies under s.7 and an operational creditor under s.9, the moratorium under s.14 follows admission, and s.29A screens who may submit a resolution plan. Under the ordinary civil route the Limitation Act governs: three years to recover money lent under Article 19 or, where payable on demand, under Article 21 from the date of the loan; twelve years to enforce payment of mortgage money under Article 62; and thirty or twelve years for a mortgagee's foreclosure or possession under Article 63. Cheque dishonour under s.138 of the Negotiable Instruments Act, 1881 remains the highest-volume recovery route of all.",
    ],
    statutes: [
      {
        act: "The Reserve Bank of India Act, 1934",
        section: "s.45IA",
        heading: "Requirement of registration and net owned fund",
        plain:
          "No NBFC may commence or carry on non-banking financial business without RBI registration and the minimum net owned fund.",
        source: "statute-library",
      },
      {
        act: "The Reserve Bank of India Act, 1934",
        section: "s.17",
        heading: "Business which the Bank may transact",
        plain:
          "Lists the businesses the RBI may carry on — accepting deposits, dealing in securities, granting advances and the rest.",
        source: "statute-library",
      },
      {
        act: "The Securities and Exchange Board of India Act, 1992",
        section: "s.12A",
        heading: "Prohibition of manipulative and deceptive devices, insider trading",
        plain:
          "Prohibits use of any manipulative or deceptive device in connection with the issue, purchase or sale of a listed security.",
        source: "statute-library",
      },
      {
        act: "The Securities and Exchange Board of India Act, 1992",
        section: "ss.15G, 15HA",
        heading: "Penalty for insider trading; for fraudulent and unfair trade practices",
        plain:
          "₹25 crore or three times the profit made, whichever is higher, under each.",
        source: "statute-library",
      },
      {
        act: "The Insolvency and Bankruptcy Code, 2016",
        section: "ss.7, 9, 14, 29A",
        heading: "CIRP initiation, moratorium and resolution-applicant eligibility",
        plain:
          "Financial creditor under s.7, operational creditor under s.9, moratorium on admission under s.14, and the s.29A eligibility bar.",
        source: "statute-library",
      },
      {
        act: "The Negotiable Instruments Act, 1881",
        section: "s.138",
        heading: "Dishonour of cheque for insufficiency of funds",
        plain:
          "Punishable with imprisonment up to two years or fine up to twice the cheque amount.",
        source: "statute-library",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Arts. 19, 21",
        heading: "Recovery of money lent; money lent payable on demand",
        plain:
          "Three years each — for money payable on demand, from the date of the loan, not the date of the demand.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Arts. 62, 63",
        heading: "Enforcing mortgage money; mortgagee's foreclosure or possession",
        plain:
          "Twelve years to enforce payment of mortgage money; thirty or twelve years for foreclosure or possession depending on the limb.",
        source: "limitation-act",
      },
      {
        act: "The Foreign Exchange Management Act, 1999",
        section: "ECB-2 / FLA / APR",
        heading: "Reporting under FEMA",
        plain:
          "Monthly ECB-2 for outstanding external commercial borrowings; annual FLA for entities with FDI or overseas investment; Annual Performance Report for ODI holders.",
        source: "compliance-calendar",
      },
    ],
    relatedFeatureSlugs: [
      "regulator-monitoring",
      "regulatory-change-feed",
      "filing-calendar",
      "contract-scanner",
      "clause-risk-scoring",
      "legal-simulators",
      "court-research-corpus",
      "citation-graph",
      "nexus-clause-coverage",
      "insurance-analyzer",
      "cross-border-tax",
      "audit-log",
    ],
    relatedToolHrefs: [
      "/calendar",
      "/tools/limitation-period-finder",
      "/tools/interest-calculator",
    ],
    relatedLimitationSlugs: [
      "recovery-of-money-lent",
      "money-lent-payable-on-demand",
      "enforce-mortgage-money",
      "mortgage-foreclosure-or-possession",
      "bill-or-note-payable-at-fixed-time",
      "surety-against-principal-debtor",
      "cheque-bounce-complaint-timeline",
      "ibc-appeal-to-nclat",
    ],
    relatedTemplateSlugs: [],
    relatedSolutionHrefs: [
      "/solutions/financial-institutions",
      "/solutions/enterprise",
      "/solutions/ca-cs",
    ],
    relatedProductHrefs: [
      "/product/compliance-ai",
      "/product/legal-ai",
      "/product/global",
    ],
    relatedCalendarRegulators: ["rbi", "fema", "sebi"],
    faqs: [
      {
        q: "When does limitation start on a loan repayable on demand?",
        a: "From the date of the loan, not from the date you make the demand. That is Article 21 of the Limitation Act, 1963, and it catches lenders who assume the clock waits for them. Article 19 covers money lent generally, also three years. A written acknowledgment of liability signed before the period expires (s.18) or a part-payment acknowledged in the payer's own signed writing (s.19) gives a fresh period; an acknowledgment of an already-barred debt does not.",
      },
      {
        q: "Which RBI and FEMA returns does the calendar carry?",
        a: "The monthly ECB-2 return for entities with outstanding external commercial borrowings, under FEMA 1999 with the RBI Master Direction on reporting and the ECB Master Direction; the annual FLA return for every Indian entity with outstanding FDI or overseas investment; and the ODI Annual Performance Report under Regulation 10 of the FEM (Overseas Investment) Regulations 2022. Each row states who it applies to — none of them is universal.",
      },
      {
        q: "Does LexVio monitor RBI and SEBI circulars?",
        a: "Yes. Regulator monitoring tracks SEBI, RBI, MCA/ROC and GST circulars and filings end to end, and the regulatory change feed summarises new circulars and lets you filter by Act, regulator and effective date. Financial institutions are one of the ten audiences with a dedicated solutions page.",
      },
      {
        q: "Can LexVio read a loan or facility agreement?",
        a: "It reads any contract — the three-tier extraction cascade handles PDF, DOCX and scanned bilingual documents, clause-level risk scoring returns red, amber or green per clause with an explanation and confidence score, and the clause coverage map shows which clauses are missing or non-standard across a whole portfolio of facility documents rather than one at a time.",
      },
    ],
  },

  // ── 11 ────────────────────────────────────────────────────────────────────
  {
    slug: "mergers-acquisitions",
    title: "Mergers & Acquisitions",
    short: "M&A",
    blurb:
      "Diligence on an Indian target is a document problem before it is a legal one: a data room of several thousand files, a reps-and-warranties package to test, indemnity caps and CPs to extract, and a Companies Act, 2013 overlay of related-party approvals under s.188, director interest disclosures under s.184 and financial statements under s.129. LexVio runs a deal-format diligence pass over the room and returns an India red-flag report.",
    covers: [
      "The corporate law of an Indian acquisition is mostly about who approved what. Section 2 of the Companies Act, 2013 carries the definitions the whole analysis turns on — related party, subsidiary, holding company, key managerial personnel. Section 188 governs related-party transactions and the consents they need, s.184 requires directors to disclose interest at the first Board meeting and annually, s.166 sets the fiduciary standard the seller's board was held to, and s.143 covers the auditor's duties including fraud reporting under s.143(12). Section 129 is the reason contingent liabilities buried in indemnity clauses eventually surface.",
      "The contract law is the warranty package. Section 17 of the Indian Contract Act, 1872 defines fraud — a suggestion of fact known to be false, active concealment, or a promise made without intent to perform — which is the backstop when a disclosure schedule turns out to be untrue. Section 124 defines the contract of indemnity that the whole indemnity architecture is built on. Where the target is distressed, s.29A of the IBC decides who is even eligible to be a resolution applicant.",
      "M&A Deal Intelligence runs a diligence pass in deal format over an uploaded data room: reps and warranties, indemnity caps, conditions precedent, MAC clauses, and an India red-flag report. Around it sit the vault mechanics a deal actually needs — ZIP ingest for thousands of documents at once, per-document share links with expiry, password and watermarking, folder-level permissions, and an immutable activity audit log.",
    ],
    statutes: [
      {
        act: "The Companies Act, 2013",
        section: "s.2",
        heading: "Definitions",
        plain:
          "Carries the definitions of related party, subsidiary, holding company and key managerial personnel that the diligence turns on.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.188",
        heading: "Related party transactions",
        plain:
          "Prior Board consent and, above thresholds, shareholder approval — the approvals a diligence pass has to find evidence of.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.184",
        heading: "Disclosure of interest by director",
        plain:
          "Disclosure at the first Board meeting and annually; absence of the disclosure is itself a finding.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.166",
        heading: "Duties of directors",
        plain:
          "Good faith, due care, no conflict of interest and no undue gain — the standard the seller's board is measured against.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.143",
        heading: "Powers and duties of auditors",
        plain:
          "Including the fraud-reporting obligation under sub-section (12), which is a diligence document in its own right.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.129",
        heading: "Financial statement",
        plain:
          "True and fair view under Schedule III, following the accounting standards under s.133.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.17",
        heading: "Fraud defined",
        plain:
          "The backstop when a disclosure schedule or warranty turns out to be untrue.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.124",
        heading: "Contract of indemnity defined",
        plain:
          "A promise to save the other from loss caused by the promisor's conduct or by any other person.",
        source: "statute-library",
      },
      {
        act: "The Securities and Exchange Board of India Act, 1992",
        section: "ss.12A, 15G",
        heading: "Insider trading and manipulative devices",
        plain:
          "Unpublished price-sensitive information in a live deal is the classic exposure; the penalty is ₹25 crore or three times the profit, whichever is higher.",
        source: "statute-library",
      },
      {
        act: "The Insolvency and Bankruptcy Code, 2016",
        section: "s.29A",
        heading: "Persons not eligible to be a resolution applicant",
        plain:
          "Screens defaulters, wilful defaulters, undischarged insolvents and connected persons out of a distressed acquisition.",
        source: "statute-library",
      },
    ],
    relatedFeatureSlugs: [
      "m-and-a-intelligence",
      "bulk-analysis",
      "bulk-upload",
      "nexus-search",
      "nexus-ask",
      "nexus-clause-coverage",
      "nexus-graph",
      "clause-risk-scoring",
      "vault-sharing",
      "folder-permissions",
      "encrypted-vault",
      "watermarks",
      "audit-log",
      "ifrs-indas",
      "cross-border-tax",
      "white-label",
    ],
    relatedToolHrefs: [
      "/library",
      "/tools/roc-forms",
      "/calendar",
      "/tools/gstin-validator",
    ],
    relatedLimitationSlugs: ["breach-of-contract-general", "residuary-application"],
    relatedTemplateSlugs: [
      "shareholders-agreement-india",
      "share-subscription-agreement-india",
      "safe-post-money-india",
      "esop-plan-and-grant-letter-india",
    ],
    relatedSolutionHrefs: [
      "/solutions/enterprise",
      "/solutions/law-firms",
      "/solutions/ca-cs",
      "/solutions/founders",
      "/solutions/financial-institutions",
    ],
    relatedProductHrefs: ["/product/legal-ai", "/product/global", "/product/tax-ai"],
    relatedCalendarRegulators: ["mca", "sebi", "fema"],
    faqs: [
      {
        q: "What does M&A Deal Intelligence actually extract from a data room?",
        a: "It runs a deal-format diligence pass and returns reps and warranties, indemnity caps, conditions precedent, MAC clauses and an India red-flag report. Underneath it, ZIP ingest handles thousands of documents in one upload with automatic parsing and indexing, and the same portfolio-level tools that work on a contract book — search, ask-across-everything, and a clause coverage map showing what is present, missing or non-standard — work on a data room.",
      },
      {
        q: "How is the data room kept confidential?",
        a: "AES-256 at rest, TLS 1.3 in transit, and AWS Mumbai (ap-south-1) as the default region with backups kept in India. Sharing is per-document with expiry, password and watermarking; folders carry role-based permissions with per-folder overrides; and every action across the workspace is written to an immutable audit log. For deployments that cannot use the cloud at all there is an air-gapped Helm install with no outbound telemetry.",
      },
      {
        q: "Which ROC filings should a buyer pull on an Indian target?",
        a: "The nine on the ROC Form Finder are the diarised set, and the ones that matter most in diligence are AOC-4 for financial statements, MGT-7 or MGT-7A for the annual return, ADT-1 for auditor appointment, DPT-3 for deposits and MSME-1 for outstanding dues to micro and small suppliers beyond 45 days. Each entry states the due rule and the late-fee ladder, so a pattern of late filings is visible rather than inferred.",
      },
      {
        q: "Does LexVio value the target or advise on deal structure?",
        a: "No. It reads documents and produces analysis with citations — the IFRS vs IndAS comparator shows the treatment of a clause or transaction under both frameworks, and cross-border tax covers DTAA treaty analysis, transfer pricing flags and Form 15CA/CB requirements. Valuation, structuring and the fairness opinion are your advisers' work, not the software's.",
      },
    ],
  },

  // ── 12 ────────────────────────────────────────────────────────────────────
  {
    slug: "data-privacy",
    title: "Data Privacy",
    short: "Data Privacy",
    blurb:
      "The DPDP Act, 2023 is in force in name and phased in substance: the Data Protection Board provisions commenced on 13 November 2025, Consent Manager registration under s.6(9) opens on 13 November 2026, and the entire operative regime — notice, consent, security safeguards, breach intimation, children's data, Data Principal rights, cross-border and the penalty Schedule — commences on 13 May 2027. Everything you build between now and then is measured against that date.",
    covers: [
      "The DPDP Act, 2023 uses its own vocabulary, and using GDPR's instead is the first mistake. There is a Data Fiduciary, a Data Processor and a Data Principal; there is no 'controller' and no Article 28. Section 4 sets the grounds for processing, s.5 the notice, and s.6 consent — with s.6(10) placing the burden of proving valid consent on the Data Fiduciary, which is why consent-record architecture matters more than consent-notice wording.",
      "Section 8 carries the general obligations: reasonable security safeguards under s.8(4)-(5), fleshed out by Rule 6 of the DPDP Rules 2025 including a one-year minimum retention for logs; breach intimation under s.8(6) with Rule 7's two-stage clock; and erasure under s.8(7)-(8) with Rule 8 and the Third Schedule. Section 9 governs children's data with verifiable parental consent under Rule 10, s.10 sets Significant Data Fiduciary obligations under Rule 13, ss.11-14 carry Data Principal rights with Rule 14's grievance mechanics, and s.16 is the cross-border restriction power that Rule 15 and Rule 13(4) operate through.",
      "The free Data Processing Agreement in the library is drafted to this Act rather than translated from GDPR: statutory Data Fiduciary / Data Processor / Data Principal vocabulary, the Rule 6 security floor including the one-year log minimum, a breach window short enough for the Fiduciary's own 72-hour Board report, and cross-border handled the way s.16 actually works. The readiness quiz is fifteen weighted questions, each carrying its own section or rule citation, with every gap grouped against the phase date that makes it enforceable.",
    ],
    statutes: [
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "s.5",
        heading: "Notice",
        plain:
          "The notice a Data Fiduciary must give a Data Principal, with the content detail supplied by Rule 3(a)-(c) of the DPDP Rules 2025.",
        source: "dpdp-dataset",
      },
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "s.6",
        heading: "Consent",
        plain:
          "Free, specific, informed, unconditional and unambiguous consent — with s.6(10) putting the burden of proof on the Data Fiduciary.",
        source: "dpdp-dataset",
      },
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "s.8(4)-(5)",
        heading: "Reasonable security safeguards",
        plain:
          "Read with Rule 6(1)(a)-(g), which supplies the security floor including the one-year minimum for logs.",
        source: "dpdp-dataset",
      },
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "s.8(6)",
        heading: "Intimation of personal data breach",
        plain:
          "Read with Rule 7(1) and 7(2) — the two-stage breach intimation clock to the Data Principal and to the Board.",
        source: "dpdp-dataset",
      },
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "s.9",
        heading: "Processing of personal data of children",
        plain:
          "Verifiable parental consent under Rule 10, guardianship for persons with disability under Rule 11, and the Fourth Schedule exemptions under Rule 12.",
        source: "dpdp-dataset",
      },
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "s.10",
        heading: "Additional obligations of a Significant Data Fiduciary",
        plain:
          "Designation criteria in s.10(1) and obligations in s.10(2), operationalised by Rule 13.",
        source: "dpdp-dataset",
      },
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "ss.11-14",
        heading: "Rights of the Data Principal",
        plain:
          "Access, correction and erasure, grievance redressal and nomination — with the mechanics in Rule 14(1)-(4).",
        source: "dpdp-dataset",
      },
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "s.16",
        heading: "Processing outside India",
        plain:
          "The cross-border restriction power, read with Rule 15 and Rule 13(4). Not a GDPR-style adequacy regime.",
        source: "dpdp-dataset",
      },
      {
        act: "The Digital Personal Data Protection Act, 2023",
        section: "ss.28-34 and the Schedule",
        heading: "Board proceedings, appeals and penalties",
        plain:
          "The enforcement machinery, in force from 13 May 2027 under G.S.R. 843(E) cl.(c) — not from the 2025 or 2026 phase dates.",
        source: "dpdp-dataset",
      },
    ],
    relatedFeatureSlugs: [
      "dpdp-readiness",
      "india-residency",
      "encrypted-vault",
      "no-training-on-your-data",
      "prompt-injection-defence",
      "audit-log",
      "folder-permissions",
      "vault-sharing",
      "watermarks",
      "on-prem",
      "contract-scanner",
      "clause-risk-scoring",
      "nexus-clause-coverage",
    ],
    relatedToolHrefs: ["/tools/dpdp-readiness-quiz", "/library"],
    relatedLimitationSlugs: [],
    relatedTemplateSlugs: ["data-processing-agreement-dpdp-india"],
    relatedSolutionHrefs: [
      "/solutions/enterprise",
      "/solutions/smb",
      "/solutions/founders",
      "/solutions/agencies",
      "/solutions/financial-institutions",
    ],
    relatedProductHrefs: ["/product/compliance-ai", "/product/legal-ai"],
    relatedCalendarRegulators: [],
    faqs: [
      {
        q: "Is the DPDP Act enforceable right now?",
        a: "Not against an ordinary Data Fiduciary. G.S.R. 843(E) commenced the Act in three tranches. On 13 November 2025 only the Data Protection Board provisions came into force — s.1(2), s.2, ss.18-26, s.35, ss.38-43 and s.44(1) and (3), plus Rules 1, 2 and 17-21. Section 27, the Board's power to entertain complaints and inquire, was not among them: that phase created the institution, not the jurisdiction. On 13 November 2026 only s.6(9) and s.27(1)(d) commence, both about Consent Manager registration. The operative regime and the penalty Schedule commence on 13 May 2027.",
      },
      {
        q: "Can I reuse my GDPR DPA in India?",
        a: "You can, and it will read wrong. The DPDP Act uses statutory terms GDPR does not — Data Fiduciary, Data Processor, Data Principal — and its obligations do not map cleanly onto Article 28. The free DPDP data processing agreement in the library is drafted to the Act and the Rules directly: the Rule 6 security floor with its one-year log minimum, a breach window short enough for the Fiduciary's own reporting clock, and cross-border handled the way s.16 actually works rather than through an adequacy analogy.",
      },
      {
        q: "How does the DPDP readiness quiz score me?",
        a: "Fifteen weighted questions, each citing its own section of the Act or rule of the DPDP Rules 2025, producing a gap list grouped by the phase date on which each gap becomes enforceable. It is a self-assessment aid — explicitly not a compliance audit, not legal advice, and not a defence. Anything the underlying research could not confirm is carried as an open item rather than filled in with something plausible.",
      },
      {
        q: "What does LexVio itself do with the documents I upload?",
        a: "Storage is AES-256 at rest and TLS 1.3 in transit, with AWS Mumbai (ap-south-1) as the default region and backups kept in India. Models are not trained on customer content without explicit opt-in, and the default is off. User-pasted text is sanitised before it reaches the model as a prompt-injection defence. For deployments that cannot use a cloud at all, the air-gapped Helm install emits no outbound telemetry.",
      },
    ],
  },

  // ── 13 ────────────────────────────────────────────────────────────────────
  {
    slug: "general-legal",
    title: "General Legal",
    short: "General Legal",
    blurb:
      "Not every legal question arrives with a practice area attached. This hub is the entry point for the ordinary ones — is this agreement enforceable, how long do I have to sue, what does this clause actually do, which filing did I just miss — and points at the free scanner, the template library, the calculators and the residuary provisions of Indian law that catch what the specific ones do not.",
    covers: [
      "Indian law has two deliberate catch-alls, and knowing them saves a lot of searching. Article 113 of the Limitation Act, 1963 gives three years for any suit for which no other article of the Schedule provides a period, running from when the right to sue accrues. Article 137 does the same for applications. If you cannot find a specific article for your claim, you have almost certainly landed on one of these two.",
      "On the contract side the residuary question is s.10 of the Indian Contract Act, 1872: an agreement is a contract if made by the free consent of parties competent to contract, for a lawful consideration and with a lawful object. Where it goes wrong, s.73 gives compensation for loss that naturally arose from the breach. And where a cheque was the payment mechanism, s.138 of the Negotiable Instruments Act, 1881 makes dishonour an offence punishable with up to two years' imprisonment or a fine up to twice the cheque amount.",
      "The free surface is genuinely free and genuinely local. The public scanner takes any pasted contract and returns a Legal Health Score and the top risks with no account. Fifteen free tools are live — GST, TDS rates, stamp duty across 22 states, HSN/SAC codes, GSTIN validation, income-tax slabs, the CII series, interest and late fees, ROC forms, limitation periods, the 1961-to-2025 section mapping, DPDP readiness, an NDA generator, the compliance calendar and the template library. The calculators run in your browser; nothing you type is uploaded. Seven contract templates are licensed for commercial use without attribution.",
    ],
    statutes: [
      {
        act: "The Limitation Act, 1963",
        section: "Art. 113",
        heading: "Residuary suit",
        plain:
          "Three years, from when the right to sue accrues — the article a suit lands on when no other fits.",
        source: "limitation-act",
      },
      {
        act: "The Limitation Act, 1963",
        section: "Art. 137",
        heading: "Residuary application",
        plain:
          "Three years, from when the right to apply accrues — the application equivalent of Article 113.",
        source: "limitation-act",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.10",
        heading: "What agreements are contracts",
        plain:
          "Free consent, competent parties, lawful consideration, lawful object. The four-part test everything else builds on.",
        source: "statute-library",
      },
      {
        act: "The Indian Contract Act, 1872",
        section: "s.73",
        heading: "Compensation for loss or damage caused by breach of contract",
        plain:
          "Compensation for loss or damage that naturally arose in the usual course of things from the breach.",
        source: "statute-library",
      },
      {
        act: "The Negotiable Instruments Act, 1881",
        section: "s.138",
        heading: "Dishonour of cheque for insufficiency of funds",
        plain:
          "Imprisonment up to two years, or fine up to twice the cheque amount, or both.",
        source: "statute-library",
      },
      {
        act: "The Companies Act, 2013",
        section: "s.2",
        heading: "Definitions",
        plain:
          "The master definitions section — company, director, key managerial personnel, related party, subsidiary, holding company.",
        source: "statute-library",
      },
    ],
    relatedFeatureSlugs: [
      "public-scanner",
      "contract-scanner",
      "legal-health-score",
      "clause-risk-scoring",
      "vio-ask",
      "contract-templates",
      "nda-generator-tool",
      "matter-management",
      "encrypted-vault",
      "whatsapp-bot",
      "voice",
      "browser-extension",
    ],
    relatedToolHrefs: [
      "/tools/nda-generator",
      "/library",
      "/calendar",
      "/tools/limitation-period-finder",
      "/tools/gst-calculator",
      "/tools/stamp-duty-calculator",
      "/tools/interest-calculator",
    ],
    relatedLimitationSlugs: ["residuary-suit", "residuary-application"],
    relatedTemplateSlugs: [
      "safe-post-money-india",
      "shareholders-agreement-india",
      "share-subscription-agreement-india",
      "esop-plan-and-grant-letter-india",
      "founders-agreement-india",
      "data-processing-agreement-dpdp-india",
      "employment-agreement-india",
    ],
    relatedSolutionHrefs: [
      "/solutions/freelancers",
      "/solutions/students",
      "/solutions/smb",
      "/solutions/founders",
    ],
    relatedProductHrefs: ["/product/legal-ai"],
    relatedCalendarRegulators: [],
    faqs: [
      {
        q: "How long do I have to sue if no specific limitation article fits?",
        a: "Three years under Article 113 of the Limitation Act, 1963 for a suit, and three years under Article 137 for an application, each running from when the right to sue or apply accrues. Both are residuary articles — they exist precisely for claims the Schedule does not name. Remember that s.5 condonation is never available for suits, so the residuary period for a suit is effectively hard.",
      },
      {
        q: "Is the free contract scanner really free, and what does it do with my document?",
        a: "It is free and needs no account: paste a contract and it returns a Legal Health Score out of 100 and the top risks. Inside the product the same pipeline runs a three-tier extraction cascade so PDFs, DOCX files and scanned bilingual documents parse, scores every clause red, amber or green with an explanation and confidence score, and suggests replacement language. Models are not trained on customer content without explicit opt-in, and the default is off.",
      },
      {
        q: "Which free tools are worth knowing about?",
        a: "Fifteen are live: GST calculator, TDS rate finder, NDA generator, stamp duty calculator across 22 states, HSN/SAC finder, GSTIN validator, income-tax section mapper for the 1961-to-2025 changeover, DPDP readiness quiz, limitation period finder, income-tax slab rates, cost inflation index, interest and late fee calculator, ROC form finder, the compliance deadline calendar and the free template library. The calculators run in your browser; nothing you type is uploaded.",
      },
      {
        q: "Can LexVio replace a lawyer?",
        a: "No, and nothing on this site claims otherwise. Every output — risk score, fix suggestion, statutory citation, simulator result — is an analytical aid that shows its sources so a qualified advocate, company secretary or chartered accountant can check it. Indian law is fact- and state-specific, and the answer to most real questions depends on facts a document reader does not have.",
      },
    ],
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

export const AREA_SLUGS: ReadonlyArray<string> = AREAS.map((a) => a.slug);

export function areaBySlug(slug: string): PracticeArea | undefined {
  return AREAS.find((a) => a.slug === slug);
}

/** Index of the area, or -1. Used for prev/next. */
export function areaIndex(slug: string): number {
  return AREAS.findIndex((a) => a.slug === slug);
}

export type AreaCounts = {
  statutes: number;
  features: number;
  tools: number;
  limitation: number;
  templates: number;
  solutions: number;
  regulators: number;
  /** Everything a hub links out to — the "what's inside" number on the card. */
  total: number;
};

export function areaCounts(a: PracticeArea): AreaCounts {
  const c = {
    statutes: a.statutes.length,
    features: a.relatedFeatureSlugs.length,
    tools: a.relatedToolHrefs.length,
    limitation: a.relatedLimitationSlugs.length,
    templates: a.relatedTemplateSlugs.length,
    solutions: a.relatedSolutionHrefs.length,
    regulators: a.relatedCalendarRegulators.length,
  };
  return {
    ...c,
    total:
      c.statutes +
      c.features +
      c.tools +
      c.limitation +
      c.templates +
      c.solutions +
      c.regulators,
  };
}
